// Crowd Movement & Historical Playback.
// Ground-up rewrite against movement.jsx prototype: single column, no map.

import { useEffect, useMemo, useRef, useState, type MouseEvent as ReactMouseEvent } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import type { Alert, DensitySnapshot, Operation, Zone } from '@vigilo/shared';

import { get } from '@vigilo/api-client';
import { useApp } from '@/shell/AppContext';
import { useMockMode } from '@/shell/MockModeContext';
import { MOCK_DASHBOARD, MOCK_DENSITY, MOCK_HISTORY, MOCK_ZONES, HIST_DAY_MS, HIST_NOW_TICK, HIST_TICK_MS, type DensityDataPoint } from '../data/mock';
import { useDensityStream } from '../hooks/useDensityStream';
import {
  isZoneMetricFresh,
  useDensityHistory,
  useFlowHistory,
  zoneMetricsDensitySnapshots,
  useLiveZoneMetrics,
  type CurrentZoneMetrics,
  type DensityHistoryPoint,
  type FlowHistoryPoint,
} from '../hooks/useLiveZoneMetrics';
import { useOperationAlertHistory } from '../hooks/useAlertHistory';
import { getHistoricalDensity } from '../hooks/usePlayback';
import { useZones } from '../hooks/useZones';
import { alertTypeLabel } from '../utils/alert-type';
import { withAlpha } from '../utils/color';
import {
  Btn,
  FONT_SIZE,
  FONT_WEIGHT,
  Icons,
  StatusDot,
  LETTER_SPACING,
  LINE_HEIGHT,
  Panel,
  RADIUS,
  SIZE,
  SPACE,
  type ColorPalette,
} from '@vigilo/design-system';
import { getCrowdC, operationStatusColor } from '../../design/crowdPalette';
import {
  CROWD_CLIENT_ACTIVITY_ACTION,
  CROWD_CLIENT_RESOURCE_TYPE,
  useCrowdActivityAudit,
} from '../hooks/useCrowdActivityAudit';
import { PaginationControls } from '../components/PaginationControls';
import { useLocalPagination } from '../hooks/useLocalPagination';

type PlaybackRange = '1h' | '6h' | '24h';
type PlaybackSpeed = '1x' | '2x' | '4x';
type DisplayZone = Zone & { zoneCode?: string };
type MovementProjectionStatus = 'simulation' | 'fresh' | 'history' | 'stale' | 'missing' | 'loading' | 'error';
type TimelineAlertState = 'pending' | Alert['status'];
type AggregateFlowMetric = Pick<
  CurrentZoneMetrics,
  'zoneId' | 'entryRate' | 'exitRate' | 'netFlow' | 'congestionScore' | 'congestionActive' | 'dominantDirection'
> & { sampledAt?: string };
interface FlowRateSeriesPoint {
  sampledAt: string;
  entryRate: number | null;
  exitRate: number | null;
  netFlow: number | null;
  sourceCount: number;
}

const PLAYBACK_LIVE = 60;
const TIMELINE_W = 1000;
const TIMELINE_H = 220;
const FLOW_W = 400;
const FLOW_H = 140;
const FLOW_CHART_WIDTH = '52%';
const ROSE_W = 260;
const ROSE_H = 220;
const ROSE_CX = 130;
const ROSE_CY = 110;
const ROSE_R = 90;
const HEATMAP_W = 1000;
const HEATMAP_H = 140;
const CHART_MAX = 100;
const MOCK_FLOW_BARS = 30;
const HEATMAP_CELLS = 60;
const RANGE_LABEL_COUNT = 5;
const MAX_PLAYBACK_WINDOW_MS = 24 * 60 * 60_000;
const PLAYBACK_RANGES: PlaybackRange[] = ['1h', '6h', '24h'];
const DIRECTION_BIN_DEGREES = 45;
const DIRECTION_STRONG_SHARE = 0.65;
const CONGESTION_MEDIUM_SCORE = 60;
const CONGESTION_HIGH_SCORE = 85;
const CONGESTION_ACTIVE_FALLBACK_SCORE = 100;
const FLOW_HISTORY_SAMPLE_TOLERANCE_MS = 5 * 60_000;
const DENSITY_HISTORY_SAMPLE_TOLERANCE_MS = 5 * 60_000;
// Density Replay: the time axis stretches around alerts (see buildReplayAxis) and the pointer magnifies what is under it.
const REPLAY_AXIS_CELLS = 600;
const REPLAY_ALERT_ROOM_PX = 112;         // width each alert should get on the axis
const REPLAY_MAX_ALERT_SHARE = 0.78;      // most of the width alerts may take, so the quiet time never disappears
const REPLAY_STRETCH_BEFORE_MS = 60_000;  // the stretch around an alert begins this long before it...
const REPLAY_STRETCH_AFTER_MS = 180_000;  // ...and lasts this long after it (the rise, peak and fall that follow)
const REPLAY_LENS = 2.2;                  // extra magnification at the pointer
const REPLAY_LENS_REACH_PX = 96;          // the pointer only magnifies within this distance of an alert
const REPLAY_LENS_EASE_MS = 140;
const REPLAY_HIT_PX = 12;                 // how close the pointer must be to an alert to open its detail
const REPLAY_LANE_ROW_H = 30;             // one row of alert labels, above the curve
const REPLAY_LANE_MAX_ROWS = 3;
const REPLAY_LABEL_H = 28;
const REPLAY_LANE_ROW_COST = 24;           // how much a label would rather slide sideways than sit one row further from the curve
const REPLAY_LABEL_MAX_SHIFT_PX = 64;     // the furthest a label may slide from straight above its alert before it is left off
const REPLAY_SEVERITY_RANK: Record<string, number> = { critical: 0, high: 1, medium: 2, low: 3 };
const REPLAY_AXIS_H = 18;                 // time labels under the curve
const REPLAY_TICK_ROOM_PX = 100;
const REPLAY_TICK_EDGE_PX = 44;
const REPLAY_TICK_STEPS_MS = [
  6 * 3_600_000, 3 * 3_600_000, 3_600_000, 1_800_000, 900_000, 600_000, 300_000, 120_000, 60_000, 30_000, 15_000, 10_000, 5_000,
];
const REPLAY_CARD_W = 272;
const COMPASS_BINS = [
  { angle: 0, label: 'N' },
  { angle: 45, label: 'NE' },
  { angle: 90, label: 'E' },
  { angle: 135, label: 'SE' },
  { angle: 180, label: 'S' },
  { angle: 225, label: 'SW' },
  { angle: 270, label: 'W' },
  { angle: 315, label: 'NW' },
] as const;

interface PlaybackWindow {
  startMs: number;
  endMs: number;
  source: 'operation' | 'route' | 'mock';
}

function fmtHHMM(ms: number): string {
  const d = new Date(ms);
  return `${String(d.getHours()).padStart(2, '0')}:${String(d.getMinutes()).padStart(2, '0')}`;
}

function zoneCode(z: DisplayZone): string {
  if (z.zoneCode) return z.zoneCode;
  const derived = z.name
    .split(/\s+/)
    .map(part => part[0])
    .filter(Boolean)
    .join('')
    .slice(0, 3)
    .toUpperCase();
  return derived || z.id.slice(0, 3).toUpperCase();
}

function rangeMs(range: PlaybackRange): number {
  if (range === '1h') return 60 * 60_000;
  if (range === '6h') return 6 * 60 * 60_000;
  return 24 * 60 * 60_000;
}

function parseDateMs(value: string | null | undefined): number | null {
  if (!value) return null;
  const ms = Date.parse(value);
  return Number.isFinite(ms) ? ms : null;
}

function formatShortDateTime(ms: number): string {
  const d = new Date(ms);
  return `${d.toLocaleDateString(undefined, { month: 'short', day: '2-digit' })} ${fmtHHMM(ms)}`;
}

function windowDurationLabel(startMs: number, endMs: number): string {
  const mins = Math.max(1, Math.round((endMs - startMs) / 60_000));
  const unit = (n: number, word: string) => `${n} ${word}${n === 1 ? '' : 's'}`;
  if (mins < 60) return unit(mins, 'Minute');
  const h = Math.floor(mins / 60);
  const m = mins % 60;
  return m ? `${unit(h, 'Hour')} ${unit(m, 'Minute')}` : unit(h, 'Hour');
}

function windowSpanLabel(startMs: number, endMs: number): string {
  const sameDay = new Date(startMs).toDateString() === new Date(endMs).toDateString();
  return sameDay
    ? `${fmtHHMM(startMs)} - ${fmtHHMM(endMs)}`
    : `${formatShortDateTime(startMs)} - ${formatShortDateTime(endMs)}`;
}

// Section heading on one line, with capitalised words (long descriptive headings are easier to read
// than tracked all-caps; caps stay reserved for short labels and badges).
// Segments flagged `key` (zone, time window) are highlighted so a first-time viewer can read
// what the chart covers; the surrounding wording is passed through unchanged.
interface TitleSegment { text: string; key?: boolean }
function ScopeTitle({ C, segments, mb = 0 }: { C: ColorPalette; segments: TitleSegment[]; mb?: number }) {
  return (
    <div style={{
      fontSize: FONT_SIZE.basePlus, lineHeight: '12.6px', fontWeight: FONT_WEIGHT.bold,
      letterSpacing: LETTER_SPACING.tight,
      color: C.sub, marginBottom: mb,
    }}>
      {segments.map((seg, i) => (
        <span key={i} style={seg.key ? { color: C.text } : undefined}>{seg.text}</span>
      ))}
    </div>
  );
}

function operationStatusLabel(status: string | undefined): string {
  if (!status) return 'Operation required';
  return status.replace(/_/g, ' ').toUpperCase();
}

function resolvePlaybackWindow(
  selectedOperation: Operation | null,
  searchParams: URLSearchParams,
): PlaybackWindow {
  const mockEndMs = HIST_DAY_MS + HIST_NOW_TICK * HIST_TICK_MS;
  const mockStartMs = Math.max(HIST_DAY_MS, mockEndMs - MAX_PLAYBACK_WINDOW_MS);
  if (!selectedOperation) return { startMs: mockStartMs, endMs: mockEndMs, source: 'mock' };

  const routeOperationId = searchParams.get('operationId');
  const isRoutePlayback = searchParams.get('mode') === 'playback'
    && (!routeOperationId || routeOperationId === selectedOperation.id);
  const routeStartMs = isRoutePlayback ? parseDateMs(searchParams.get('from')) : null;
  const routeEndMs = isRoutePlayback ? parseDateMs(searchParams.get('to')) : null;

  const operationStartMs = parseDateMs(selectedOperation.actualStart ?? selectedOperation.scheduledStart);
  const scheduledEndMs = parseDateMs(selectedOperation.scheduledEnd);
  const operationEndMs = selectedOperation.actualEnd
    ? parseDateMs(selectedOperation.actualEnd)
    : selectedOperation.status === 'active' || selectedOperation.status === 'paused'
      ? Math.min(Date.now(), scheduledEndMs ?? Date.now())
      : scheduledEndMs;

  const rawStartMs = routeStartMs ?? operationStartMs ?? mockStartMs;
  const rawEndMs = routeEndMs ?? operationEndMs ?? mockEndMs;
  const safeEndMs = rawEndMs > rawStartMs ? rawEndMs : mockEndMs;
  const safeStartMs = rawEndMs > rawStartMs ? rawStartMs : mockStartMs;
  const clampedStartMs = Math.max(safeStartMs, safeEndMs - MAX_PLAYBACK_WINDOW_MS);

  return {
    startMs: clampedStartMs,
    endMs: safeEndMs,
    source: routeStartMs && routeEndMs ? 'route' : 'operation',
  };
}

function allowedPlaybackRanges(windowMs: number): PlaybackRange[] {
  const allowed = PLAYBACK_RANGES.filter(r => rangeMs(r) <= windowMs);
  return allowed.length > 0 ? allowed : ['1h'];
}

function nearestHistoryValue(history: DensityDataPoint[], ms: number): DensityDataPoint | null {
  if (history.length === 0) return null;
  const startMs = new Date(history[0].timestamp).getTime();
  const rawIdx = Math.round((ms - startMs) / HIST_TICK_MS);
  return history[Math.max(0, Math.min(history.length - 1, rawIdx))] ?? null;
}

function densityHistoryPointToDataPoint(point: DensityHistoryPoint): DensityDataPoint {
  return {
    timestamp: point.sampledAt,
    occupancyCount: point.occupancyCount,
    capacityUtilisation: point.capacityUtilisation,
  };
}

function densityHistoryPointToSnapshot(point: DensityHistoryPoint): DensitySnapshot {
  return {
    zoneId: point.zoneId,
    occupancyCount: point.occupancyCount,
    densityScore: point.densityScore,
    capacityUtilisation: point.capacityUtilisation,
    trendDirection: 'stable',
    trendRate: 0,
    timestamp: point.sampledAt,
  };
}

function nearestDensityHistoryPoint(
  history: DensityHistoryPoint[],
  ms: number,
  toleranceMs = Number.POSITIVE_INFINITY,
): DensityHistoryPoint | null {
  let nearest: { point: DensityHistoryPoint; distance: number } | null = null;
  for (const point of history) {
    const sampledMs = Date.parse(point.sampledAt);
    if (!Number.isFinite(sampledMs)) continue;
    const distance = Math.abs(sampledMs - ms);
    if (!nearest || distance < nearest.distance) nearest = { point, distance };
  }
  if (!nearest || nearest.distance > toleranceMs) return null;
  return nearest?.point ?? null;
}

function sampleDensityHistory(history: DensityHistoryPoint[], startMs: number, endMs: number, count: number): DensityDataPoint[] {
  if (history.length === 0) return [];
  return Array.from({ length: count }, (_, i) => {
    const pct = count === 1 ? 1 : i / (count - 1);
    const point = nearestDensityHistoryPoint(
      history,
      startMs + pct * (endMs - startMs),
      DENSITY_HISTORY_SAMPLE_TOLERANCE_MS,
    );
    return point ? densityHistoryPointToDataPoint(point) : null;
  }).filter((point): point is DensityDataPoint => Boolean(point));
}

function sampleHistory(zoneId: string, startMs: number, endMs: number, count: number): DensityDataPoint[] {
  const history = MOCK_HISTORY[zoneId] ?? [];
  if (history.length === 0) return [];
  return Array.from({ length: count }, (_, i) => {
    const pct = count === 1 ? 1 : i / (count - 1);
    return nearestHistoryValue(history, startMs + pct * (endMs - startMs)) ?? history[0];
  });
}

function fallbackDensityPoint(zoneId: string, timestampMs: number): DensityDataPoint {
  const snap = MOCK_DENSITY[zoneId];
  const capacityUtilisation = snap?.capacityUtilisation ?? 0;
  return {
    timestamp: new Date(timestampMs).toISOString(),
    occupancyCount: snap?.occupancyCount ?? 0,
    capacityUtilisation,
  };
}

function movementSamples(
  zoneId: string,
  startMs: number,
  endMs: number,
  count: number,
  historyByZone?: Record<string, DensityHistoryPoint[]>,
  isMock = true,
): DensityDataPoint[] {
  const projectionSamples = sampleDensityHistory(historyByZone?.[zoneId] ?? [], startMs, endMs, count);
  if (projectionSamples.length >= 2) return projectionSamples;
  if (!isMock) return [];

  const samples = sampleHistory(zoneId, startMs, endMs, count);
  if (samples.length >= 2) return samples;
  const safeCount = Math.max(2, count);
  return Array.from({ length: safeCount }, (_, i) => {
    const pct = safeCount === 1 ? 1 : i / (safeCount - 1);
    return samples[i] ?? fallbackDensityPoint(zoneId, startMs + pct * (endMs - startMs));
  });
}

function playbackPositionMs(playbackTime: number, startMs: number, endMs: number): number {
  return startMs + (playbackTime / PLAYBACK_LIVE) * (endMs - startMs);
}

function densityAvg(density: Record<string, DensitySnapshot>, zones: DisplayZone[]): number {
  const snapshots = zones.map(z => density[z.id]).filter((snap): snap is DensitySnapshot => Boolean(snap));
  if (snapshots.length === 0) return 0;
  return snapshots.reduce((sum, snap) => sum + snap.capacityUtilisation, 0) / snapshots.length;
}

function densityTotalOccupancy(density: Record<string, DensitySnapshot>, zones: DisplayZone[]): number {
  return zones.reduce((sum, z) => sum + (density[z.id]?.occupancyCount ?? 0), 0);
}

function safeRate(value: number | null | undefined): number {
  return Math.max(0, value ?? 0);
}

function hasFlowProjection(row: AggregateFlowMetric): boolean {
  return row.entryRate !== null || row.exitRate !== null || row.netFlow !== null;
}

function hasDirectionProjection(row: AggregateFlowMetric): boolean {
  return row.dominantDirection !== null;
}

function hasCongestionProjection(row: AggregateFlowMetric): boolean {
  return row.congestionScore !== null || row.congestionActive !== null;
}

function congestionLevel(score: number): 'high' | 'medium' | 'low' {
  if (score >= CONGESTION_HIGH_SCORE) return 'high';
  if (score >= CONGESTION_MEDIUM_SCORE) return 'medium';
  return 'low';
}

function normalizeBearing(degrees: number): number {
  return ((degrees % 360) + 360) % 360;
}

function directionBinIndex(degrees: number): number {
  return Math.round(normalizeBearing(degrees) / DIRECTION_BIN_DEGREES) % COMPASS_BINS.length;
}

function directionMagnitude(row: AggregateFlowMetric): number {
  const volume = safeRate(row.entryRate) + safeRate(row.exitRate);
  const net = Math.abs(row.netFlow ?? (safeRate(row.entryRate) - safeRate(row.exitRate)));
  return Math.max(1, Math.round(volume || net));
}

function directionBinsFromMetrics(C: ColorPalette, metrics: AggregateFlowMetric[]) {
  const bins = COMPASS_BINS.map(bin => ({ ...bin, magnitude: 0, color: C.muted }));
  for (const row of metrics) {
    if (row.dominantDirection === null || !Number.isFinite(row.dominantDirection)) continue;
    const bin = bins[directionBinIndex(row.dominantDirection)];
    if (bin) bin.magnitude += directionMagnitude(row);
  }
  const max = Math.max(1, ...bins.map(bin => bin.magnitude));
  return bins.map(bin => ({
    ...bin,
    color: bin.magnitude === 0
      ? C.muted
      : bin.magnitude / max >= DIRECTION_STRONG_SHARE
        ? C.amber
        : C.teal,
  }));
}

function nearestFlowHistoryPoint(history: FlowHistoryPoint[], ms: number): FlowHistoryPoint | null {
  let nearest: { point: FlowHistoryPoint; distance: number } | null = null;
  for (const point of history) {
    const sampledMs = Date.parse(point.sampledAt);
    if (!Number.isFinite(sampledMs)) continue;
    const distance = Math.abs(sampledMs - ms);
    if (!nearest || distance < nearest.distance) nearest = { point, distance };
  }
  if (!nearest || nearest.distance > FLOW_HISTORY_SAMPLE_TOLERANCE_MS) return null;
  return nearest.point;
}

function sampleFlowHistory(
  historyByZone: Record<string, FlowHistoryPoint[]>,
  zones: DisplayZone[],
  startMs: number,
  endMs: number,
  count: number,
): FlowRateSeriesPoint[] {
  if (zones.length === 0 || endMs <= startMs) return [];
  const safeCount = Math.max(2, count);
  return Array.from({ length: safeCount }, (_, i) => {
    const pct = safeCount === 1 ? 1 : i / (safeCount - 1);
    const targetMs = startMs + pct * (endMs - startMs);
    const points = zones
      .map(zone => nearestFlowHistoryPoint(historyByZone[zone.id] ?? [], targetMs))
      .filter((point): point is FlowHistoryPoint => Boolean(point && hasFlowProjection(point)));

    if (points.length === 0) {
      return {
        sampledAt: new Date(targetMs).toISOString(),
        entryRate: null,
        exitRate: null,
        netFlow: null,
        sourceCount: 0,
      };
    }

    const entryRate = points.reduce((sum, point) => sum + safeRate(point.entryRate), 0);
    const exitRate = points.reduce((sum, point) => sum + safeRate(point.exitRate), 0);
    const netFlow = points.reduce(
      (sum, point) => sum + (point.netFlow ?? (safeRate(point.entryRate) - safeRate(point.exitRate))),
      0,
    );
    return {
      sampledAt: new Date(targetMs).toISOString(),
      entryRate,
      exitRate,
      netFlow,
      sourceCount: points.length,
    };
  });
}

function mockDirectionBins(C: ColorPalette, density: Record<string, DensitySnapshot>, zones: DisplayZone[]) {
  const total = Math.max(1, densityTotalOccupancy(density, zones));
  return [
    { angle: 0, label: 'N', magnitude: Math.round(total * 0.05), color: C.teal },
    { angle: 45, label: 'NE', magnitude: Math.round(total * 0.04), color: C.teal },
    { angle: 90, label: 'E', magnitude: Math.round(total * 0.09), color: C.amber },
    { angle: 135, label: 'SE', magnitude: Math.round(total * 0.02), color: C.teal },
    { angle: 180, label: 'S', magnitude: Math.round(total * 0.01), color: C.muted },
    { angle: 225, label: 'SW', magnitude: Math.round(total * 0.01), color: C.muted },
    { angle: 270, label: 'W', magnitude: Math.round(total * 0.03), color: C.teal },
    { angle: 315, label: 'NW', magnitude: Math.round(total * 0.06), color: C.amber },
  ];
}

function movementProjectionStatus(
  rows: CurrentZoneMetrics[],
  loading: boolean,
  error: string | null,
  isMock: boolean,
): MovementProjectionStatus {
  if (isMock) return 'simulation';
  if (error) return 'error';
  if (loading && rows.length === 0) return 'loading';
  if (rows.some(row => isZoneMetricFresh(row))) return 'fresh';
  return rows.length > 0 ? 'stale' : 'missing';
}

function projectionStatusText(status: MovementProjectionStatus): string {
  if (status === 'simulation') return 'Simulation';
  if (status === 'fresh') return 'Live';
  if (status === 'history') return 'History';
  if (status === 'stale') return 'Stale';
  if (status === 'loading') return 'Loading';
  if (status === 'error') return 'Unavailable';
  return 'No data';
}

function projectionStatusColor(C: ColorPalette, status: MovementProjectionStatus): string {
  if (status === 'fresh') return C.green;
  if (status === 'history') return C.teal;
  if (status === 'stale' || status === 'missing') return C.amber;
  if (status === 'error') return C.ember;
  return C.muted;
}

const alertLabel = alertTypeLabel;

function alertStateAtPlayback(alert: Alert, playbackMs: number): TimelineAlertState {
  const triggeredMs = Date.parse(alert.triggeredAt);
  if (!Number.isFinite(triggeredMs) || playbackMs < triggeredMs) return 'pending';

  let state: TimelineAlertState = 'open';
  const trail = alert.triggerContext?.activityTrail ?? [];
  const entries = trail
    .map(entry => ({ action: entry.action, timestampMs: Date.parse(entry.timestamp) }))
    .filter(entry => Number.isFinite(entry.timestampMs) && entry.timestampMs <= playbackMs)
    .sort((a, b) => a.timestampMs - b.timestampMs);

  for (const entry of entries) {
    if (entry.action === 'created' || entry.action === 'reopened') state = 'open';
    else if (entry.action === 'acknowledged') state = 'acknowledged';
    else if (entry.action === 'escalated') state = 'escalated';
    else if (entry.action === 'resolved') state = 'resolved';
  }

  return state;
}

function alertStateLabel(state: TimelineAlertState): string {
  // The same words the Alerts tab uses for the lifecycle, so an alert reads the same on every screen.
  if (state === 'pending') return 'PENDING';
  if (state === 'acknowledged') return 'ACKNOWLEDGED';
  if (state === 'escalated') return 'ESCALATED';
  if (state === 'resolved') return 'RESOLVED';
  return 'OPEN';
}

/** Width of the state chip beside a replay marker: wide enough for the longest word at the chart's label size. */
function stateChipWidth(label: string): number {
  return Math.max(56, Math.round(label.length * 6.6 + 14));
}

function alertMarkerColor(C: ColorPalette, state: TimelineAlertState): string {
  if (state === 'pending') return C.muted;
  if (state === 'acknowledged') return C.amber;
  if (state === 'escalated') return C.critical;
  if (state === 'resolved') return C.green;
  return C.ember;
}

// ── Density Replay: an alert-aware time axis ─────────────────────────────────
// Alerts arrive in bursts (a gate story raises its surge, its early warning and its overcrowding alert within minutes), so on an
// evenly spaced hour or day their markers and labels land on top of each other. The replay therefore stretches the time around
// alerts and squeezes the quiet time between them. The curve is drawn through the very same axis, so it stays one continuous
// line. The labels sit in their own lane above the curve (never on it) and the pointer magnifies whatever is under it.

/** "20:41:05": the time of day to the second, for the moments the chart points at. */
function fmtHHMMSS(ms: number): string {
  return `${fmtHHMM(ms)}:${String(new Date(ms).getSeconds()).padStart(2, '0')}`;
}

interface ReplayAlertMark {
  alert: Alert;
  id: string;
  ms: number;
  label: string;
}

function replayAlertMarks(alerts: Alert[], zoneId: string, startMs: number, endMs: number): ReplayAlertMark[] {
  return alerts
    .filter(alert => alert.zoneId === zoneId)
    .map(alert => ({ alert, id: alert.id, ms: Date.parse(alert.triggeredAt), label: alertLabel(alert.alertType) }))
    .filter(mark => Number.isFinite(mark.ms) && mark.ms >= startMs && mark.ms <= endMs)
    .sort((a, b) => a.ms - b.ms);
}

/** What happened to an alert and when, from its own activity trail (or, without one, its own stamps). */
function replayAlertLifecycle(alert: Alert): { text: string; ms: number }[] {
  const words: Record<string, string> = {
    created: 'Opened', acknowledged: 'Acknowledged', escalated: 'Escalated', resolved: 'Resolved', reopened: 'Reopened',
  };
  const fromTrail = (alert.triggerContext?.activityTrail ?? [])
    .filter(entry => words[entry.action] !== undefined)
    .map(entry => ({ text: words[entry.action], ms: Date.parse(entry.timestamp) }))
    .filter(entry => Number.isFinite(entry.ms));
  if (fromTrail.length > 0) return fromTrail.sort((a, b) => a.ms - b.ms);
  const stamps: Array<[string, string | null | undefined]> = [
    ['Opened', alert.triggeredAt], ['Acknowledged', alert.acknowledgedAt], ['Escalated', alert.escalatedAt], ['Resolved', alert.resolvedAt],
  ];
  return stamps
    .map(([text, at]) => ({ text, ms: at ? Date.parse(at) : Number.NaN }))
    .filter(entry => Number.isFinite(entry.ms))
    .sort((a, b) => a.ms - b.ms);
}

interface ReplayPoint { ms: number; value: number }

/** The readings the curve is drawn through: every reading of the window, not a 60-point resample, so a short story keeps its shape once it is stretched. */
function replaySeries(history: DensityHistoryPoint[] | undefined, fallback: DensityDataPoint[], startMs: number, endMs: number): ReplayPoint[] {
  const raw = (history ?? [])
    .map(point => ({ ms: Date.parse(point.sampledAt), value: Number(point.capacityUtilisation) }))
    .filter(point => Number.isFinite(point.ms) && Number.isFinite(point.value) && point.ms >= startMs && point.ms <= endMs)
    .sort((a, b) => a.ms - b.ms);
  if (raw.length >= 2) return raw;
  return fallback
    .map(point => ({ ms: Date.parse(point.timestamp), value: Number(point.capacityUtilisation) }))
    .filter(point => Number.isFinite(point.ms) && Number.isFinite(point.value));
}

/** The curve's value at a moment (between two readings it is interpolated); null when no reading is close enough. */
function replayValueAt(series: ReplayPoint[], ms: number): number | null {
  if (series.length === 0) return null;
  const last = series.length - 1;
  if (ms <= series[0].ms) return series[0].ms - ms <= DENSITY_HISTORY_SAMPLE_TOLERANCE_MS ? series[0].value : null;
  if (ms >= series[last].ms) return ms - series[last].ms <= DENSITY_HISTORY_SAMPLE_TOLERANCE_MS ? series[last].value : null;
  let lo = 0;
  let hi = last;
  while (hi - lo > 1) {
    const mid = (lo + hi) >> 1;
    if (series[mid].ms <= ms) lo = mid; else hi = mid;
  }
  const before = series[lo];
  const after = series[hi];
  if (after.ms - before.ms > 2 * DENSITY_HISTORY_SAMPLE_TOLERANCE_MS) return null;
  return before.value + (after.value - before.value) * ((ms - before.ms) / (after.ms - before.ms));
}

/**
 * The replay's time axis: where each moment of the window sits, left to right. Ordinary time counts 1; around every alert the
 * time counts more (a ramp that begins a minute before it and fades three minutes after it), so a burst of alerts gets room and
 * the quiet hours between bursts are squeezed. With no alerts the axis is an even line, exactly as it was.
 * `xs` holds where REPLAY_AXIS_CELLS + 1 evenly spaced moments land, from 0 to the full width.
 */
interface ReplayAxis {
  startMs: number;
  spanMs: number;
  xs: number[];
}

function buildReplayAxis(startMs: number, endMs: number, width: number, alertMs: number[]): ReplayAxis {
  const spanMs = Math.max(1, endMs - startMs);
  const cellMs = spanMs / REPLAY_AXIS_CELLS;
  const lift = new Array<number>(REPLAY_AXIS_CELLS).fill(0);
  let liftTotal = 0;
  for (const at of alertMs) {
    const first = Math.max(0, Math.floor((at - REPLAY_STRETCH_BEFORE_MS - startMs) / cellMs));
    const last = Math.min(REPLAY_AXIS_CELLS - 1, Math.ceil((at + REPLAY_STRETCH_AFTER_MS - startMs) / cellMs));
    for (let cell = first; cell <= last; cell++) {
      const offset = startMs + (cell + 0.5) * cellMs - at;
      const amount = offset < 0 ? 1 + offset / REPLAY_STRETCH_BEFORE_MS : 1 - offset / REPLAY_STRETCH_AFTER_MS;
      if (amount > 0) {
        lift[cell] += amount;
        liftTotal += amount;
      }
    }
  }
  // Alerts together take the share of the width they need to be read (a fixed room each), never so much that the quiet time vanishes.
  const share = Math.min(REPLAY_MAX_ALERT_SHARE, (alertMs.length * REPLAY_ALERT_ROOM_PX) / Math.max(1, width));
  const gain = liftTotal > 0 ? ((share / (1 - share)) * REPLAY_AXIS_CELLS) / liftTotal : 0;
  const weights = lift.map(amount => 1 + gain * amount);
  const total = weights.reduce((sum, weight) => sum + weight, 0);
  const xs = [0];
  let running = 0;
  for (const weight of weights) {
    running += weight;
    xs.push((running / total) * width);
  }
  return { startMs, spanMs, xs };
}

function replayAxisX(axis: ReplayAxis, ms: number): number {
  const cells = axis.xs.length - 1;
  const at = Math.max(0, Math.min(cells, ((ms - axis.startMs) / axis.spanMs) * cells));
  const cell = Math.min(cells - 1, Math.floor(at));
  return axis.xs[cell] + (axis.xs[cell + 1] - axis.xs[cell]) * (at - cell);
}

function replayAxisMs(axis: ReplayAxis, x: number): number {
  let lo = 0;
  let hi = axis.xs.length - 1;
  while (hi - lo > 1) {
    const mid = (lo + hi) >> 1;
    if (axis.xs[mid] <= x) lo = mid; else hi = mid;
  }
  const run = axis.xs[hi] - axis.xs[lo];
  const into = run > 0 ? Math.max(0, Math.min(1, (x - axis.xs[lo]) / run)) : 0;
  return axis.startMs + ((lo + into) / (axis.xs.length - 1)) * axis.spanMs;
}

/**
 * A magnifier under the pointer: it spreads what is near `center` and gathers what is far, keeps the ends of the chart where they
 * are, and never moves the point under the pointer. The curve, the alerts, the playhead and the time labels all go through it.
 */
interface ReplayLens { center: number; boost: number }

function replayLensX(x: number, width: number, lens: ReplayLens | null): number {
  if (!lens || lens.boost <= 0.001) return x;
  const offset = x - lens.center;
  const reach = offset >= 0 ? width - lens.center : lens.center;
  if (reach <= 0) return x;
  const distance = Math.min(1, Math.abs(offset) / reach);
  const spread = ((lens.boost + 1) * distance) / (lens.boost * distance + 1);
  return lens.center + Math.sign(offset) * spread * reach;
}

interface ReplayLabelBox { id: string; left: number; row: number; width: number }

/**
 * Where each alert's label goes in the lane above the curve. Labels never overlap: each goes as near to straight above its alert as
 * it can, in the row nearest the curve that has room, sliding a little sideways if it must. When even that is not possible the
 * alert keeps its dot but not its label (the more severe, then the newer, alerts are placed first), and moving over the dot or
 * magnifying that part of the chart makes room for it.
 */
function replayLabelLayout(items: { id: string; anchorX: number; width: number; rank: number }[], width: number, rows: number): ReplayLabelBox[] {
  const gap = SPACE[6];
  const taken: Array<Array<[number, number]>> = Array.from({ length: Math.max(1, rows) }, () => []);
  const placed: ReplayLabelBox[] = [];
  for (const item of [...items].sort((a, b) => a.rank - b.rank || b.anchorX - a.anchorX)) {
    const room = Math.max(0, width - item.width);
    const wanted = Math.max(0, Math.min(item.anchorX - item.width / 2, room));
    let bestRow = -1;
    let bestLeft = 0;
    let bestCost = Number.POSITIVE_INFINITY;
    for (let row = 0; row < taken.length; row++) {
      const spans = taken[row];
      const options = [wanted, ...spans.flatMap(([from, to]) => [to + gap, from - gap - item.width])];
      for (const option of options) {
        const left = Math.max(0, Math.min(option, room));
        if (Math.abs(left - wanted) > REPLAY_LABEL_MAX_SHIFT_PX) continue;
        if (!spans.every(([from, to]) => left + item.width + gap <= from || left >= to + gap)) continue;
        const cost = Math.abs(left - wanted) + row * REPLAY_LANE_ROW_COST;
        if (cost < bestCost) {
          bestRow = row;
          bestLeft = left;
          bestCost = cost;
        }
      }
    }
    if (bestRow === -1) continue;
    taken[bestRow].push([bestLeft, bestLeft + item.width]);
    placed.push({ id: item.id, left: bestLeft, row: bestRow, width: item.width });
  }
  return placed;
}

/** Time labels along the bottom: round times, as many as the room under each allows, finer where the axis is stretched. */
function replayTicks(xOf: (ms: number) => number, startMs: number, endMs: number, width: number): { x: number; label: string }[] {
  const offsetMs = new Date(startMs).getTimezoneOffset() * 60_000;
  const ticks: { x: number; label: string }[] = [];
  for (const step of REPLAY_TICK_STEPS_MS) {
    if ((endMs - startMs) / step > 4000) continue;
    const first = Math.ceil((startMs + offsetMs) / step) * step - offsetMs;
    for (let at = first; at < endMs; at += step) {
      const x = xOf(at);
      if (x < REPLAY_TICK_EDGE_PX || x > width - REPLAY_TICK_EDGE_PX) continue;
      if (xOf(Math.min(endMs, at + step)) - x < REPLAY_TICK_ROOM_PX) continue;
      if (ticks.some(tick => Math.abs(tick.x - x) < REPLAY_TICK_ROOM_PX)) continue;
      ticks.push({ x, label: step >= 60_000 ? fmtHHMM(at) : fmtHHMMSS(at) });
    }
  }
  return ticks;
}

function FlowStat({ C, label, value, unit, color, trend }: {
  C: ColorPalette;
  label: string;
  value: string;
  unit: string;
  color: string;
  trend?: 'up' | 'stable';
}) {
  return (
    <div>
      <div style={{
        fontSize: FONT_SIZE.xs,
        lineHeight: '12.6px',
        color: C.sub,
        letterSpacing: LETTER_SPACING.label,
        textTransform: 'uppercase',
        fontWeight: FONT_WEIGHT.bold,
        marginBottom: SPACE[4],
      }}>{label}</div>
      <div style={{ display: 'flex', alignItems: 'baseline', gap: SPACE[4] }}>
        <span style={{
          fontSize: FONT_SIZE.display,
          fontWeight: FONT_WEIGHT.bold,
          color,
          lineHeight: LINE_HEIGHT.tight,
          fontVariantNumeric: 'tabular-nums',
        }}>{value}</span>
        <span style={{ fontSize: FONT_SIZE.base, lineHeight: '14px', color: C.muted }}>{unit}</span>
        {trend && (
          <span style={{ marginLeft: 'auto', color: trend === 'up' ? C.ember : C.muted }}>
            {trend === 'up' ? <Icons.ArrowU size={SIZE.iconSm} /> : <Icons.ArrowR size={SIZE.iconSm} />}
          </span>
        )}
      </div>
    </div>
  );
}

function ProjectionStatusPill({ C, status }: { C: ColorPalette; status: MovementProjectionStatus }) {
  const color = projectionStatusColor(C, status);
  return (
    <span style={{
      fontSize: FONT_SIZE.xs,
      color,
      background: status === 'fresh' ? C.greenDim : status === 'error' ? C.emberDim : C.faint,
      border: `1px solid ${color}44`,
      borderRadius: RADIUS.sm,
      padding: `${SPACE[3]}px ${SPACE[7]}px`,
      fontWeight: FONT_WEIGHT.bold,
      letterSpacing: LETTER_SPACING.wide,
      textTransform: 'uppercase',
      whiteSpace: 'nowrap',
    }}>
      {projectionStatusText(status)}
    </span>
  );
}

function BigTimelineChart({ C, zoneId, alerts, playbackTime, playing, playbackSpeed, startMs, endMs, historyByZone, isMock, historyLoading, historyError, liveLabel, onPlayPause, onSeek, onSpeedChange }: {
  C: ColorPalette;
  zoneId: string;
  alerts: Alert[];
  playbackTime: number;
  playing: boolean;
  playbackSpeed: PlaybackSpeed;
  startMs: number;
  endMs: number;
  historyByZone: Record<string, DensityHistoryPoint[]>;
  isMock: boolean;
  historyLoading: boolean;
  historyError: string | null;
  liveLabel: string;
  onPlayPause: () => void;
  onSeek: (value: number) => void;
  onSpeedChange: (speed: PlaybackSpeed) => void;
}) {
  const chartRef = useRef<HTMLDivElement>(null);
  const lensCenterRef = useRef(0);
  const lensStrengthRef = useRef(0);
  const [width, setWidth] = useState(TIMELINE_W);
  const [pointer, setPointer] = useState<{ x: number; y: number } | null>(null);
  const [lensStrength, setLensStrength] = useState(0);

  const data = useMemo(
    () => movementSamples(zoneId, startMs, endMs, HEATMAP_CELLS, historyByZone, isMock),
    [zoneId, startMs, endMs, historyByZone, isMock],
  );
  const series = useMemo(
    () => replaySeries(historyByZone[zoneId], data, startMs, endMs),
    [historyByZone, zoneId, data, startMs, endMs],
  );
  const marks = useMemo(
    () => replayAlertMarks(alerts, zoneId, startMs, endMs),
    [alerts, zoneId, startMs, endMs],
  );
  const axis = useMemo(
    () => buildReplayAxis(startMs, endMs, width, marks.map(mark => mark.ms)),
    [startMs, endMs, width, marks],
  );

  // The chart is drawn at its real width (one unit is one pixel), so its text keeps its size on any screen.
  useEffect(() => {
    const element = chartRef.current;
    if (!element) return undefined;
    const measure = () => setWidth(Math.max(320, Math.round(element.clientWidth)));
    measure();
    if (typeof ResizeObserver === 'undefined') return undefined;
    const observer = new ResizeObserver(measure);
    observer.observe(element);
    return () => observer.disconnect();
  }, []);

  // The magnifier only works near an alert, and eases in and out so the chart never snaps.
  const lensTarget = pointer !== null && marks.some(mark => Math.abs(replayAxisX(axis, mark.ms) - pointer.x) <= REPLAY_LENS_REACH_PX) ? 1 : 0;
  useEffect(() => {
    if (lensStrengthRef.current === lensTarget) return undefined;
    const reduceMotion = typeof window.matchMedia === 'function' && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    let frame = 0;
    let last = performance.now();
    const step = (now: number) => {
      const current = lensStrengthRef.current;
      const change = reduceMotion ? 1 : Math.min(64, now - last) / REPLAY_LENS_EASE_MS;
      last = now;
      const next = current < lensTarget ? Math.min(lensTarget, current + change) : Math.max(lensTarget, current - change);
      lensStrengthRef.current = next;
      setLensStrength(next);
      if (next !== lensTarget) frame = requestAnimationFrame(step);
    };
    frame = requestAnimationFrame(step);
    return () => cancelAnimationFrame(frame);
  }, [lensTarget]);

  const trackPointer = (event: ReactMouseEvent<HTMLDivElement>) => {
    const rect = event.currentTarget.getBoundingClientRect();
    const x = Math.max(0, Math.min(rect.width, event.clientX - rect.left));
    lensCenterRef.current = x;
    setPointer({ x, y: event.clientY - rect.top });
  };

  if (data.length < 2) {
    const message = historyLoading
      ? 'Loading density history'
      : historyError ?? 'No density history for the selected Operation window';
    return (
      <div ref={chartRef}>
        <svg viewBox={`0 0 ${TIMELINE_W} ${TIMELINE_H}`} style={{ width: '100%', height: TIMELINE_H, display: 'block' }}>
          {[25, 50, 75].map(y => (
            <line
              key={y}
              x1="0"
              y1={TIMELINE_H - (y / CHART_MAX) * (TIMELINE_H - SPACE[20]) - SPACE[10]}
              x2={TIMELINE_W}
              y2={TIMELINE_H - (y / CHART_MAX) * (TIMELINE_H - SPACE[20]) - SPACE[10]}
              stroke={C.border2}
              strokeDasharray={`${SPACE[2]},${SPACE[3]}`}
              strokeWidth="0.6"
            />
          ))}
          <text
            x={TIMELINE_W / 2}
            y={TIMELINE_H / 2}
            fill={C.sub}
            fontSize={FONT_SIZE.sm}
            fontWeight={FONT_WEIGHT.semibold}
            textAnchor="middle"
          >
            {message}
          </text>
        </svg>
      </div>
    );
  }

  const positionMs = playbackPositionMs(playbackTime, startMs, endMs);
  const projectedSnapshot = nearestDensityHistoryPoint(
    historyByZone[zoneId] ?? [],
    positionMs,
    DENSITY_HISTORY_SAMPLE_TOLERANCE_MS,
  );
  const snapshot = projectedSnapshot
    ? densityHistoryPointToSnapshot(projectedSnapshot)
    : isMock
      ? getHistoricalDensity(zoneId, new Date(positionMs), MOCK_HISTORY)
      : null;
  const cursorVal = snapshot?.capacityUtilisation
    ?? data[Math.min(data.length - 1, Math.floor((playbackTime / PLAYBACK_LIVE) * data.length))]?.capacityUtilisation
    ?? 0;

  // Every x on the chart goes through the stretched axis and then the magnifier, so the curve, the alerts and the playhead agree.
  const eased = lensStrength * lensStrength * (3 - 2 * lensStrength);
  const lens: ReplayLens | null = eased > 0.001 ? { center: lensCenterRef.current, boost: REPLAY_LENS * eased } : null;
  const xOf = (ms: number) => replayLensX(replayAxisX(axis, ms), width, lens);

  const views = marks.map(mark => {
    const state = alertStateAtPlayback(mark.alert, positionMs);
    const stateText = `${fmtHHMMSS(mark.ms)} · ${alertStateLabel(state)}`;
    const typeW = Math.max(72, Math.round(mark.label.length * 6.4 + 16));
    const stateW = stateChipWidth(stateText);
    return {
      mark,
      state,
      stateText,
      typeW,
      stateW,
      boxW: Math.max(typeW, stateW),
      color: alertMarkerColor(C, state),
      baseX: replayAxisX(axis, mark.ms),
      x: xOf(mark.ms),
      value: replayValueAt(series, mark.ms) ?? cursorVal,
      rank: REPLAY_SEVERITY_RANK[mark.alert.severity] ?? 4,
    };
  });

  // The lane of labels takes as many rows as the unmagnified chart needs, and keeps them while the magnifier moves.
  const baseBoxes = replayLabelLayout(views.map(view => ({ id: view.mark.id, anchorX: view.baseX, width: view.boxW, rank: view.rank })), width, REPLAY_LANE_MAX_ROWS);
  const laneRows = baseBoxes.reduce((rows, box) => Math.max(rows, box.row + 1), 0);
  const laneH = laneRows > 0 ? laneRows * REPLAY_LANE_ROW_H + SPACE[6] : 0;
  const boxes = lens ? replayLabelLayout(views.map(view => ({ id: view.mark.id, anchorX: view.x, width: view.boxW, rank: view.rank })), width, laneRows) : baseBoxes;
  const boxOf = new Map(boxes.map(box => [box.id, box] as const));
  const rowTop = (row: number) => (laneRows - 1 - row) * REPLAY_LANE_ROW_H + SPACE[1];

  const plotH = TIMELINE_H - SPACE[20];
  const yOf = (value: number) => laneH + TIMELINE_H - (value / CHART_MAX) * plotH - SPACE[10];
  const baseY = yOf(0);
  const svgH = laneH + TIMELINE_H + REPLAY_AXIS_H;

  const curve = series.map(point => `${xOf(point.ms).toFixed(1)},${yOf(point.value).toFixed(1)}`).join(' ');
  const curveFrom = series.length > 0 ? xOf(series[0].ms) : 0;
  const curveTo = series.length > 0 ? xOf(series[series.length - 1].ms) : width;

  const cursorX = xOf(positionMs);
  const cursorY = yOf(cursorVal);
  const tooltipX = Math.max(30, Math.min(width - 30, cursorX));
  const pillAbove = cursorY - 38 >= laneH + SPACE[2];
  const pillY = pillAbove ? cursorY - 38 : cursorY + SPACE[14];

  const ticks = replayTicks(xOf, startMs, endMs, width);
  const replayLabel = playbackTime === PLAYBACK_LIVE ? liveLabel : `t-${Math.max(0, Math.round((endMs - positionMs) / 60_000))}m`;

  // What the pointer is on: the alert whose dot or label it is nearest (alerts at the same moment open together).
  const candidates = pointer
    ? views
      .map(view => {
        const box = boxOf.get(view.mark.id);
        const top = box ? rowTop(box.row) : 0;
        const onLabel = box !== undefined
          && pointer.x >= box.left && pointer.x <= box.left + box.width
          && pointer.y >= top && pointer.y <= top + REPLAY_LABEL_H;
        return { view, distance: onLabel ? 0 : Math.abs(view.x - pointer.x) };
      })
      .filter(candidate => candidate.distance <= REPLAY_HIT_PX)
      .sort((a, b) => a.distance - b.distance)
    : [];
  const hoveredView = candidates[0]?.view ?? null;
  const hoveredViews = hoveredView ? views.filter(view => view === hoveredView || Math.abs(view.x - hoveredView.x) <= 4) : [];
  const hoveredIds = new Set(hoveredViews.map(view => view.mark.id));
  const hiddenLabels = views.length - boxes.length;
  const hoverMs = pointer ? replayAxisMs(axis, pointer.x) : null;
  const hoverLabel = hoverMs === null ? '' : fmtHHMMSS(hoverMs);
  const roundOne = (value: number) => Math.round(value * 10) / 10;

  return (
    <div style={{ position: 'relative' }}>
      <div
        ref={chartRef}
        onMouseMove={trackPointer}
        onMouseLeave={() => setPointer(null)}
        style={{ position: 'relative' }}
      >
        <svg viewBox={`0 0 ${width} ${svgH}`} style={{ width: '100%', height: svgH, display: 'block' }}>
          {[25, 50, 75].map(y => (
            <g key={y}>
              <line
                x1="0"
                y1={yOf(y)}
                x2={width}
                y2={yOf(y)}
                stroke={C.border2}
                strokeDasharray={`${SPACE[2]},${SPACE[3]}`}
                strokeWidth="0.6"
              />
              <text
                x={SPACE[4]}
                y={yOf(y) - SPACE[3]}
                fill={C.sub}
                fontSize={FONT_SIZE.label}
                fontFamily="monospace"
              >{y}%</text>
            </g>
          ))}
          <line
            x1="0"
            y1={yOf(85)}
            x2={width}
            y2={yOf(85)}
            stroke={C.ember}
            strokeDasharray={`${SPACE[4]},${SPACE[2]}`}
            strokeWidth="0.8"
            opacity="0.7"
          />
          <text
            x={SPACE[4]}
            y={yOf(85) - SPACE[4]}
            fill={C.ember}
            fontSize={FONT_SIZE.label}
            fontWeight={FONT_WEIGHT.bold}
          >ALERT 85%</text>

          <polygon points={`${curveFrom},${baseY} ${curve} ${curveTo},${baseY}`} fill={withAlpha(C.amber, 0.13)} />
          <polyline points={curve} fill="none" stroke={C.amber} strokeWidth="2" strokeLinejoin="round" />

          {/* Alerts sit on the curve as small dots; their labels live in the lane above it, joined by a thin leader, so nothing covers the line. */}
          {views.map(view => {
            const box = boxOf.get(view.mark.id);
            const dotY = yOf(view.value);
            const active = hoveredIds.has(view.mark.id);
            const labelBottom = box ? rowTop(box.row) + REPLAY_LABEL_H : 0;
            const leaderX = box ? Math.max(box.left + SPACE[6], Math.min(box.left + box.width - SPACE[6], view.x)) : view.x;
            return (
              <g key={view.mark.id}>
                <line x1={view.x} y1={dotY} x2={view.x} y2={baseY} stroke={view.color} strokeWidth="1" strokeDasharray={`${SPACE[2]},${SPACE[3]}`} opacity="0.3" />
                {box && <line x1={leaderX} y1={labelBottom} x2={view.x} y2={dotY} stroke={view.color} strokeWidth={active ? 1.5 : 1} opacity={active ? 0.95 : 0.6} />}
              </g>
            );
          })}
          {views.map(view => {
            const dotY = yOf(view.value);
            const active = hoveredIds.has(view.mark.id);
            return (
              <g key={view.mark.id}>
                {active && <circle cx={view.x} cy={dotY} r={SPACE[9]} fill="none" stroke={view.color} strokeWidth="1.5" opacity="0.8" />}
                <circle cx={view.x} cy={dotY} r={SPACE[5]} fill={view.color} stroke={C.surface} strokeWidth="2" />
              </g>
            );
          })}
          {views.map(view => {
            const box = boxOf.get(view.mark.id);
            if (!box) return null;
            const top = rowTop(box.row);
            const active = hoveredIds.has(view.mark.id);
            return (
              <g key={view.mark.id} role="img" aria-label={`${view.mark.label}, ${view.stateText}`}>
                <rect x={box.left} y={top} width={view.typeW} height={SPACE[14]} fill={view.color} stroke={active ? C.text : 'none'} strokeWidth="1.5" rx={RADIUS.xs} />
                <text x={box.left + view.typeW / 2} y={top + SPACE[10]} fill="#fff" fontSize={FONT_SIZE.label} fontWeight={FONT_WEIGHT.bold} textAnchor="middle">{view.mark.label}</text>
                <rect x={box.left} y={top + SPACE[14] + SPACE[2]} width={view.stateW} height={SPACE[12]} fill={C.surface} stroke={active ? C.text : view.color} strokeWidth={active ? 1.5 : 1} rx={RADIUS.xs} />
                <text x={box.left + view.stateW / 2} y={top + SPACE[14] + SPACE[2] + SPACE[9]} fill={view.color} fontSize={FONT_SIZE.label} fontWeight={FONT_WEIGHT.bold} textAnchor="middle">{view.stateText}</text>
              </g>
            );
          })}

          {/* Time along the bottom: round times where there is room for them, finer where the axis is stretched. */}
          <text x={0} y={baseY + SPACE[13]} fill={C.muted} fontSize={FONT_SIZE.base} fontFamily="monospace" textAnchor="start">{fmtHHMM(startMs)}</text>
          <text x={width} y={baseY + SPACE[13]} fill={C.muted} fontSize={FONT_SIZE.base} fontFamily="monospace" textAnchor="end">{fmtHHMM(endMs)}</text>
          {ticks.map(tick => (
            <g key={`${tick.x.toFixed(0)}-${tick.label}`}>
              <line x1={tick.x} y1={baseY} x2={tick.x} y2={baseY + SPACE[3]} stroke={C.border2} strokeWidth="1" />
              <text x={tick.x} y={baseY + SPACE[13]} fill={C.muted} fontSize={FONT_SIZE.base} fontFamily="monospace" textAnchor="middle">{tick.label}</text>
            </g>
          ))}

          {pointer && pointer.y >= laneH && pointer.y <= baseY && (
            <g pointerEvents="none">
              <line x1={pointer.x} y1={laneH} x2={pointer.x} y2={baseY} stroke={C.sub} strokeWidth="1" strokeDasharray={`${SPACE[2]},${SPACE[3]}`} opacity="0.7" />
              <rect x={Math.max(0, Math.min(width - 56, pointer.x - 28))} y={baseY + SPACE[2]} width="56" height={SPACE[14]} fill={C.surface} stroke={C.border2} rx={RADIUS.xs} />
              <text x={Math.max(28, Math.min(width - 28, pointer.x))} y={baseY + SPACE[13]} fill={C.text} fontSize={FONT_SIZE.base} fontFamily="monospace" fontWeight={FONT_WEIGHT.bold} textAnchor="middle">{hoverLabel}</text>
            </g>
          )}

          <line x1={cursorX} y1={laneH} x2={cursorX} y2={baseY} stroke={C.text} strokeWidth="1" />
          <circle cx={cursorX} cy={cursorY} r={SPACE[6]} fill={C.amber} stroke={C.surface} strokeWidth="2" />
          <rect x={tooltipX - 30} y={pillY} width="60" height={SPACE[20]} fill={C.amber} rx={RADIUS.sm} />
          <text x={tooltipX} y={pillY + SPACE[14]} fill="#fff" fontSize={FONT_SIZE.base} fontWeight={FONT_WEIGHT.bold} textAnchor="middle">{cursorVal.toFixed(0)}%</text>
        </svg>

        {hoveredView && (
          <div style={{
            position: 'absolute',
            left: Math.max(SPACE[4], Math.min(width - REPLAY_CARD_W - SPACE[4], hoveredView.x - REPLAY_CARD_W / 2)),
            top: svgH + SPACE[4],
            width: REPLAY_CARD_W,
            zIndex: 5,
            pointerEvents: 'none',
            background: C.surface,
            border: `1px solid ${C.border2}`,
            borderRadius: RADIUS.panel,
            boxShadow: '0 6px 18px rgba(0,0,0,0.18)',
            padding: `${SPACE[6]}px ${SPACE[12]}px`,
          }}>
            {hoveredViews.slice(0, 3).map((view, index) => {
              const context = view.mark.alert.triggerContext;
              const unit = context?.unit ?? '';
              const reading = context && Number.isFinite(context.currentValue) && Number.isFinite(context.thresholdValue)
                ? `Reading ${roundOne(context.currentValue)}${unit} against ${roundOne(context.thresholdValue)}${unit}`
                : null;
              return (
                <div key={view.mark.id} style={{ padding: `${SPACE[6]}px 0`, borderTop: index > 0 ? `1px solid ${C.border}` : 'none' }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: SPACE[6], fontSize: FONT_SIZE.body, fontWeight: FONT_WEIGHT.bold, color: C.text }}>
                    <span style={{ width: SPACE[8], height: SPACE[8], borderRadius: RADIUS.pill, background: view.color, flexShrink: 0 }} />
                    <span>{view.mark.label}</span>
                    <span style={{ marginLeft: 'auto', fontSize: FONT_SIZE.xs, letterSpacing: LETTER_SPACING.wide, color: view.color }}>{alertStateLabel(view.state)}</span>
                  </div>
                  {reading && <div style={{ fontSize: FONT_SIZE.base, color: C.sub, marginTop: SPACE[3] }}>{reading}</div>}
                  <div style={{ marginTop: SPACE[3] }}>
                    {replayAlertLifecycle(view.mark.alert).slice(0, 5).map((step, stepIndex) => (
                      <div key={`${step.text}-${stepIndex}`} style={{ display: 'flex', gap: SPACE[8], fontSize: FONT_SIZE.base, lineHeight: '15px', color: C.muted }}>
                        <span style={{ fontFamily: 'monospace', color: C.sub }}>{fmtHHMMSS(step.ms)}</span>
                        <span>{step.text}</span>
                      </div>
                    ))}
                  </div>
                </div>
              );
            })}
            {hoveredViews.length > 3 && (
              <div style={{ fontSize: FONT_SIZE.base, color: C.muted, paddingTop: SPACE[4] }}>+{hoveredViews.length - 3} more at this moment</div>
            )}
          </div>
        )}
      </div>

      {marks.length > 0 && (
        <div style={{ fontSize: FONT_SIZE.xs, lineHeight: '12px', color: C.muted, marginTop: SPACE[4] }}>
          Time is stretched around alerts so each one can be read. Move over an alert to magnify it and see when it opened, was acknowledged and resolved.
          {hiddenLabels > 0 ? ` ${hiddenLabels} label${hiddenLabels === 1 ? ' is' : 's are'} left off where alerts are very close together; move over them to see each.` : ''}
        </div>
      )}

      <div style={{
        marginTop: SPACE[14],
        padding: `${SPACE[10]}px ${SPACE[14]}px`,
        background: C.surface,
        border: `1px solid ${C.border}`,
        borderRadius: RADIUS.card,
        display: 'flex',
        alignItems: 'center',
        gap: SPACE[14],
      }}>
        <button
          onClick={onPlayPause}
          style={{
            width: SPACE[32],
            height: SPACE[32],
            borderRadius: RADIUS.pill,
            border: 'none',
            background: C.amber,
            color: '#fff',
            cursor: 'pointer',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            flexShrink: 0,
          }}
        >
          {playing ? <Icons.Pause size={SIZE.iconSm} /> : <Icons.Play size={SIZE.iconSm} />}
        </button>
        <span style={{
          fontSize: FONT_SIZE.base,
          color: C.amber,
          fontWeight: FONT_WEIGHT.bold,
          letterSpacing: LETTER_SPACING.wide,
          textTransform: 'uppercase',
          minWidth: 48,
        }}>{replayLabel}</span>
        <input
          type="range"
          min="0"
          max={PLAYBACK_LIVE}
          value={playbackTime}
          onChange={e => onSeek(Number(e.target.value))}
          style={{ flex: 1, accentColor: C.amber }}
        />
        <div style={{ display: 'flex', gap: SPACE[4] }}>
          {(['1x', '2x', '4x'] as PlaybackSpeed[]).map(speed => (
            <button
              key={speed}
              onClick={() => onSpeedChange(speed)}
              style={{
                fontSize: FONT_SIZE.xs,
                fontWeight: FONT_WEIGHT.bold,
                padding: `${SPACE[4]}px ${SPACE[8]}px`,
                borderRadius: RADIUS.sm,
                background: playbackSpeed === speed ? C.amberDim : 'transparent',
                border: `1px solid ${playbackSpeed === speed ? C.amber + '55' : C.border2}`,
                color: playbackSpeed === speed ? C.amber : C.muted,
                cursor: 'pointer',
                fontFamily: 'monospace',
              }}
            >{speed}</button>
          ))}
        </div>
      </div>
    </div>
  );
}

function EntryExitChart({ C, density, zones, series, isMock, hasFlowProjectionAvailable, emptyLabel }: {
  C: ColorPalette;
  density: Record<string, DensitySnapshot>;
  zones: DisplayZone[];
  series: FlowRateSeriesPoint[];
  isMock: boolean;
  hasFlowProjectionAvailable: boolean;
  emptyLabel: string;
}) {
  if (!hasFlowProjectionAvailable) {
    return (
      <svg viewBox={`0 0 ${FLOW_W} ${FLOW_H}`} style={{ width: FLOW_CHART_WIDTH, minWidth: 320, maxWidth: FLOW_W, height: FLOW_H, display: 'block', margin: '0 auto' }}>
        {[25, 50, 75].map(y => (
          <line
            key={y}
            x1="0"
            y1={FLOW_H - (y / CHART_MAX) * (FLOW_H - SPACE[10]) - SPACE[5]}
            x2={FLOW_W}
            y2={FLOW_H - (y / CHART_MAX) * (FLOW_H - SPACE[10]) - SPACE[5]}
            stroke={C.border2}
            strokeDasharray={`${SPACE[2]},${SPACE[3]}`}
            strokeWidth="0.5"
          />
        ))}
        <text
          x={FLOW_W / 2}
          y={FLOW_H / 2}
          fill={C.sub}
          fontSize={FONT_SIZE.sm}
          fontWeight={FONT_WEIGHT.semibold}
          textAnchor="middle"
        >
          {emptyLabel}
        </text>
      </svg>
    );
  }

  if (isMock) {
    const avgUtil = densityAvg(density, zones);
    const inbound = Array.from({ length: MOCK_FLOW_BARS }, (_, i) => avgUtil * 0.6 + Math.sin(i * 0.3) * 20 + i * 4);
    const outbound = Array.from({ length: MOCK_FLOW_BARS }, (_, i) => avgUtil * 0.35 + Math.cos(i * 0.4) * 15 + i * 1.5);
    const max = Math.max(...inbound) * 1.1;
    const bw = (FLOW_W - SPACE[4]) / MOCK_FLOW_BARS;

    return (
      <svg viewBox={`0 0 ${FLOW_W} ${FLOW_H}`} style={{ width: FLOW_CHART_WIDTH, minWidth: 320, maxWidth: FLOW_W, height: FLOW_H, display: 'block', margin: '0 auto' }}>
        {[25, 50, 75].map(y => (
          <line
            key={y}
            x1="0"
            y1={FLOW_H - (y / CHART_MAX) * (FLOW_H - SPACE[10]) - SPACE[5]}
            x2={FLOW_W}
            y2={FLOW_H - (y / CHART_MAX) * (FLOW_H - SPACE[10]) - SPACE[5]}
            stroke={C.border2}
            strokeDasharray={`${SPACE[2]},${SPACE[3]}`}
            strokeWidth="0.5"
          />
        ))}
        {inbound.map((v, i) => (
          <rect
            key={`mock-in-${i}`}
            x={i * bw + SPACE[1]}
            y={FLOW_H - (v / max) * (FLOW_H - SPACE[10]) - SPACE[5]}
            width={bw - SPACE[2]}
            height={(v / max) * (FLOW_H - SPACE[10])}
            fill={C.teal}
            opacity="0.85"
            rx={RADIUS.xs}
          />
        ))}
        {outbound.map((v, i) => (
          <rect
            key={`mock-out-${i}`}
            x={i * bw + SPACE[1]}
            y={FLOW_H - (v / max) * (FLOW_H - SPACE[10]) - SPACE[5]}
            width={bw - SPACE[2]}
            height={SPACE[3]}
            fill={C.amber}
            rx={RADIUS.xs}
          />
        ))}
        <polyline
          points={outbound.map((v, i) => `${i * bw + bw / 2},${FLOW_H - (v / max) * (FLOW_H - SPACE[10]) - SPACE[5]}`).join(' ')}
          fill="none"
          stroke={C.amber}
          strokeWidth="1.5"
        />
      </svg>
    );
  }

  const rows = series;
  const rowsWithData = rows.filter(row => row.sourceCount > 0);
  if (rowsWithData.length === 0) {
    return (
      <svg viewBox={`0 0 ${FLOW_W} ${FLOW_H}`} style={{ width: FLOW_CHART_WIDTH, minWidth: 320, maxWidth: FLOW_W, height: FLOW_H, display: 'block', margin: '0 auto' }}>
        <text
          x={FLOW_W / 2}
          y={FLOW_H / 2}
          fill={C.sub}
          fontSize={FONT_SIZE.sm}
          fontWeight={FONT_WEIGHT.semibold}
          textAnchor="middle"
        >
          {emptyLabel}
        </text>
      </svg>
    );
  }

  const max = Math.max(1, ...rowsWithData.flatMap(row => [safeRate(row.entryRate), safeRate(row.exitRate)])) * 1.1;
  const bw = (FLOW_W - SPACE[4]) / Math.max(1, rows.length);

  return (
    <svg viewBox={`0 0 ${FLOW_W} ${FLOW_H}`} style={{ width: FLOW_CHART_WIDTH, minWidth: 320, maxWidth: FLOW_W, height: FLOW_H, display: 'block', margin: '0 auto' }}>
      {[25, 50, 75].map(y => (
        <line
          key={y}
          x1="0"
          y1={FLOW_H - (y / CHART_MAX) * (FLOW_H - SPACE[10]) - SPACE[5]}
          x2={FLOW_W}
          y2={FLOW_H - (y / CHART_MAX) * (FLOW_H - SPACE[10]) - SPACE[5]}
          stroke={C.border2}
          strokeDasharray={`${SPACE[2]},${SPACE[3]}`}
          strokeWidth="0.5"
        />
      ))}
      {rows.map((row, i) => row.sourceCount > 0 ? (
        <rect
          key={`flow-in-${row.sampledAt}`}
          x={i * bw + SPACE[1]}
          y={FLOW_H - (safeRate(row.entryRate) / max) * (FLOW_H - SPACE[10]) - SPACE[5]}
          width={bw - SPACE[2]}
          height={(safeRate(row.entryRate) / max) * (FLOW_H - SPACE[10])}
          fill={C.teal}
          opacity="0.85"
          rx={RADIUS.xs}
        />
      ) : null)}
      {rows.map((row, i) => row.sourceCount > 0 ? (
        <rect
          key={`flow-out-${row.sampledAt}`}
          x={i * bw + SPACE[1]}
          y={FLOW_H - (safeRate(row.exitRate) / max) * (FLOW_H - SPACE[10]) - SPACE[5]}
          width={bw - SPACE[2]}
          height={SPACE[3]}
          fill={C.amber}
          rx={RADIUS.xs}
        />
      ) : null)}
      <polyline
        points={rows
          .map((row, i) => row.sourceCount > 0
            ? `${i * bw + bw / 2},${FLOW_H - (safeRate(row.exitRate) / max) * (FLOW_H - SPACE[10]) - SPACE[5]}`
            : null)
          .filter((point): point is string => Boolean(point))
          .join(' ')}
        fill="none"
        stroke={C.amber}
        strokeWidth="1.5"
      />
    </svg>
  );
}

function DirectionalRose({ C, density, zones, metrics, isMock, hasDirectionProjectionAvailable, emptyLabel }: {
  C: ColorPalette;
  density: Record<string, DensitySnapshot>;
  zones: DisplayZone[];
  metrics: AggregateFlowMetric[];
  isMock: boolean;
  hasDirectionProjectionAvailable: boolean;
  emptyLabel: string;
}) {
  if (!hasDirectionProjectionAvailable) {
    return (
      <svg viewBox={`0 0 ${ROSE_W} ${ROSE_H}`} style={{ width: '100%', height: 200 }}>
        {[0.25, 0.5, 0.75, 1].map(p => (
          <circle key={p} cx={ROSE_CX} cy={ROSE_CY} r={ROSE_R * p} fill="none" stroke={C.border2} strokeWidth="0.5" strokeDasharray={`${SPACE[2]},${SPACE[3]}`} />
        ))}
        <text
          x={ROSE_CX}
          y={ROSE_CY}
          fill={C.sub}
          fontSize={FONT_SIZE.sm}
          fontWeight={FONT_WEIGHT.semibold}
          textAnchor="middle"
        >
          {emptyLabel}
        </text>
      </svg>
    );
  }

  const directions = isMock
    ? mockDirectionBins(C, density, zones)
    : directionBinsFromMetrics(C, metrics);
  const maxC = Math.max(1, ...directions.map(d => d.magnitude));

  return (
    <svg viewBox={`0 0 ${ROSE_W} ${ROSE_H}`} style={{ width: '100%', height: 200 }}>
      {[0.25, 0.5, 0.75, 1].map(p => (
        <circle key={p} cx={ROSE_CX} cy={ROSE_CY} r={ROSE_R * p} fill="none" stroke={C.border2} strokeWidth="0.5" strokeDasharray={`${SPACE[2]},${SPACE[3]}`} />
      ))}
      {directions.map(d => {
        const len = (d.magnitude / maxC) * ROSE_R;
        const rad = (d.angle - 90) * Math.PI / 180;
        const x2 = ROSE_CX + Math.cos(rad) * len;
        const y2 = ROSE_CY + Math.sin(rad) * len;
        const lx = ROSE_CX + Math.cos(rad) * (ROSE_R + SPACE[12]);
        const ly = ROSE_CY + Math.sin(rad) * (ROSE_R + SPACE[12]);
        return (
          <g key={d.label}>
            <line x1={ROSE_CX} y1={ROSE_CY} x2={x2} y2={y2} stroke={d.color} strokeWidth={SIZE.barLg} strokeLinecap="round" opacity="0.85" />
            <text x={lx} y={ly + SPACE[3]} fill={C.muted} fontSize={FONT_SIZE.xs} fontWeight={FONT_WEIGHT.bold} textAnchor="middle">{d.label}</text>
          </g>
        );
      })}
      <circle cx={ROSE_CX} cy={ROSE_CY} r={SPACE[4]} fill={C.text} />
      <text x="230" y="50" fill={C.muted} fontSize={FONT_SIZE.label} fontWeight={FONT_WEIGHT.bold} letterSpacing={LETTER_SPACING.klabel}>FLOW</text>
      <text x="230" y="62" fill={C.text} fontSize={FONT_SIZE.label} fontFamily="monospace">max {maxC.toLocaleString()}/min</text>
    </svg>
  );
}

function ProjectionStatusNote({ C, status }: { C: ColorPalette; status: MovementProjectionStatus }) {
  if (status === 'fresh' || status === 'history' || status === 'simulation') return null;
  const text = status === 'stale'
    ? 'Latest movement data is stale.'
    : status === 'loading'
      ? 'Loading movement data.'
      : status === 'error'
        ? 'Movement data is unavailable.'
        : 'No movement data for the selected Operation.';
  return (
    <div style={{
      fontSize: FONT_SIZE.label,
      color: projectionStatusColor(C, status),
      marginTop: SPACE[8],
    }}>
      {text}
    </div>
  );
}

function ZoneHeatmap({ C, zones, startMs, endMs, playbackTime, historyByZone, isMock, historyLoading, historyError, cursorLabel }: {
  C: ColorPalette;
  zones: DisplayZone[];
  startMs: number;
  endMs: number;
  playbackTime: number;
  historyByZone: Record<string, DensityHistoryPoint[]>;
  isMock: boolean;
  historyLoading: boolean;
  historyError: string | null;
  cursorLabel: string;
}) {
  if (zones.length === 0) {
    return (
      <div style={{
        height: HEATMAP_H,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        color: C.muted,
        fontSize: FONT_SIZE.sm,
      }}>
        No zones assigned to the current operation.
      </div>
    );
  }

  const cellW = HEATMAP_W / HEATMAP_CELLS;
  const rowH = HEATMAP_H / zones.length;
  const cursorX = SPACE[28] + (playbackTime / PLAYBACK_LIVE) * (HEATMAP_W - SPACE[28]);
  const rows = zones.map(z => ({
    zone: z,
    samples: movementSamples(z.id, startMs, endMs, HEATMAP_CELLS, historyByZone, isMock),
  }));
  const hasRows = rows.some(row => row.samples.length > 0);
  const emptyText = historyLoading
    ? 'Loading density history'
    : historyError ?? 'No density history for the selected Operation window';

  return (
    <svg viewBox={`0 0 ${HEATMAP_W} ${HEATMAP_H}`} style={{ width: '100%', height: HEATMAP_H }}>
      {!hasRows && (
        <text x={HEATMAP_W / 2} y={HEATMAP_H / 2} fill={C.muted} fontSize={FONT_SIZE.sm} fontWeight={FONT_WEIGHT.semibold} textAnchor="middle">
          {emptyText}
        </text>
      )}
      {rows.map(({ zone: z, samples }, ri) => {
        return (
          <g key={z.id}>
            <text x="0" y={ri * rowH + rowH / 2 + SPACE[3]} fill={C.muted} fontSize={FONT_SIZE.label} fontFamily="monospace">{zoneCode(z)}</text>
            {samples.map((pt, i) => {
              const v = pt.capacityUtilisation;
              const col = v > 85 ? C.ember : v > 70 ? C.amber : v > 40 ? C.teal : C.green;
              const sampledMs = Date.parse(pt.timestamp);
              const xPct = Number.isFinite(sampledMs) && endMs > startMs
                ? Math.max(0, Math.min(1, (sampledMs - startMs) / (endMs - startMs)))
                : i / Math.max(1, samples.length - 1);
              return (
                <rect
                  key={i}
                  x={SPACE[28] + xPct * (HEATMAP_W - SPACE[28])}
                  y={ri * rowH + SPACE[2]}
                  width={cellW * 0.85}
                  height={rowH - SPACE[4]}
                  fill={col}
                  opacity={0.2 + (v / CHART_MAX) * 0.7}
                />
              );
            })}
          </g>
        );
      })}
      <line x1={cursorX} y1="0" x2={cursorX} y2={HEATMAP_H} stroke={C.text} strokeWidth="1" strokeDasharray={`${SPACE[3]},${SPACE[2]}`} />
      <text x={cursorX} y="-2" fill={C.text} fontSize={FONT_SIZE.label} textAnchor="middle">{cursorLabel}</text>
    </svg>
  );
}

export function MovementScreen() {
  const { theme, selectedOperation } = useApp();
  const { isMock } = useMockMode();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const recordActivity = useCrowdActivityAudit();
  const C = getCrowdC(theme);

  const liveZones = useZones();
  const liveDensity = useDensityStream();
  const liveZoneMetrics = useLiveZoneMetrics(
    selectedOperation?.id ?? null,
    !isMock && selectedOperation?.status === 'active',
  );

  const allZones = (isMock ? MOCK_ZONES : liveZones.zones) as DisplayZone[];
  const zones = useMemo(
    () => selectedOperation
      ? allZones.filter(z => selectedOperation.assignedZoneIds.includes(z.id))
      : allZones,
    [allZones, selectedOperation],
  );
  const scopedZoneIds = useMemo(() => new Set(zones.map(z => z.id)), [zones]);
  const scopedZoneMetricRows = useMemo(
    () => liveZoneMetrics.rows.filter(row => scopedZoneIds.has(row.zoneId)),
    [liveZoneMetrics.rows, scopedZoneIds],
  );
  const freshZoneMetricRows = useMemo(
    () => scopedZoneMetricRows.filter(row => isZoneMetricFresh(row)),
    [scopedZoneMetricRows],
  );
  const flowRows = useMemo(
    () => freshZoneMetricRows.filter(hasFlowProjection),
    [freshZoneMetricRows],
  );
  const directionRows = useMemo(
    () => freshZoneMetricRows.filter(hasDirectionProjection),
    [freshZoneMetricRows],
  );
  const congestionMetricRows = useMemo(
    () => freshZoneMetricRows.filter(hasCongestionProjection),
    [freshZoneMetricRows],
  );
  const zoneMetricSnapshots = useMemo(
    () => zoneMetricsDensitySnapshots(freshZoneMetricRows),
    [freshZoneMetricRows],
  );
  const projectionStatus = movementProjectionStatus(
    scopedZoneMetricRows,
    liveZoneMetrics.loading,
    liveZoneMetrics.error,
    isMock,
  );
  const hasFreshDirectionProjection = isMock || directionRows.length > 0;
  const directionProjectionStatus: MovementProjectionStatus = isMock
    ? 'simulation'
    : hasFreshDirectionProjection
      ? 'fresh'
      : projectionStatus === 'fresh'
        ? 'missing'
        : projectionStatus;
  const hasFreshCongestionProjection = isMock || congestionMetricRows.length > 0;
  const congestionProjectionStatus: MovementProjectionStatus = isMock
    ? 'simulation'
    : hasFreshCongestionProjection
      ? 'fresh'
      : projectionStatus === 'fresh'
        ? 'missing'
        : projectionStatus;
  const density = isMock
    ? MOCK_DENSITY
    : { ...liveDensity.snapshots, ...zoneMetricSnapshots };

  const [zoneId, setZoneId] = useState('');
  const [range, setRange] = useState<PlaybackRange>('1h');
  const [playbackTime, setPlaybackTime] = useState(PLAYBACK_LIVE);
  const [playing, setPlaying] = useState(false);
  const [playbackSpeed, setPlaybackSpeed] = useState<PlaybackSpeed>('1x');

  const playbackWindow = useMemo(
    () => resolvePlaybackWindow(selectedOperation, searchParams),
    [searchParams, selectedOperation],
  );
  const playbackWindowMs = Math.max(0, playbackWindow.endMs - playbackWindow.startMs);
  const enabledRanges = useMemo(
    () => allowedPlaybackRanges(playbackWindowMs),
    [playbackWindowMs],
  );
  const rangeEndMs = playbackWindow.endMs;
  const rangeStartMs = Math.max(playbackWindow.startMs, rangeEndMs - rangeMs(range));
  const positionMs = playbackPositionMs(playbackTime, rangeStartMs, rangeEndMs);
  const selectedOperationId = selectedOperation?.id ?? null;
  const historyFromIso = useMemo(() => new Date(rangeStartMs).toISOString(), [rangeStartMs]);
  const historyToIso = useMemo(() => new Date(rangeEndMs).toISOString(), [rangeEndMs]);
  const densityHistory = useDensityHistory(
    selectedOperationId,
    historyFromIso,
    historyToIso,
    !isMock && Boolean(selectedOperationId),
  );
  const flowHistory = useFlowHistory(
    selectedOperationId,
    historyFromIso,
    historyToIso,
    !isMock && Boolean(selectedOperationId),
  );
  const alertHistory = useOperationAlertHistory(
    selectedOperationId,
    historyFromIso,
    historyToIso,
    !isMock && Boolean(selectedOperationId),
  );
  const playbackAlerts = useMemo(
    () => alertHistory.alerts.filter(alert =>
      alert.operationId === selectedOperationId
      && scopedZoneIds.has(alert.zoneId),
    ),
    [alertHistory.alerts, scopedZoneIds, selectedOperationId],
  );
  const selectedZone = zones.find(z => z.id === zoneId) ?? zones.find(z => z.type === 'entry_exit_gate') ?? zones[0] ?? MOCK_ZONES[0];
  const playbackModeLabel = playbackWindow.source === 'route' ? 'Historical playback' : 'Live movement';
  const terminalPlaybackLabel = playbackWindow.source === 'route' ? 'End' : 'Live';
  const timelineCursorLabel = playbackWindow.source === 'route' ? 'cursor' : 'now';
  const playbackWindowLabel = `${formatShortDateTime(rangeStartMs)} - ${formatShortDateTime(rangeEndMs)}`;
  const operationWindowLabel = `${formatShortDateTime(playbackWindow.startMs)} - ${formatShortDateTime(playbackWindow.endMs)}`;
  const windowScopeLabel = `${playbackWindow.source === 'route' ? '' : 'Last '}${windowDurationLabel(rangeStartMs, rangeEndMs)}${playbackWindow.source === 'route' ? ' window' : ''} (${windowSpanLabel(rangeStartMs, rangeEndMs)})`;
  const historyLoading = densityHistory.loading || flowHistory.loading;
  const historyError = densityHistory.error ?? flowHistory.error;
  const historySourceLabel = isMock
    ? 'Simulation history'
    : historyLoading
      ? 'Loading anonymous aggregate history'
      : historyError
        ? 'Anonymous aggregate history unavailable'
        : densityHistory.rows.length > 0 && flowHistory.rows.length > 0
          ? 'Anonymous aggregate density and flow history'
          : densityHistory.rows.length > 0
            ? 'Anonymous aggregate density history'
            : flowHistory.rows.length > 0
              ? 'Anonymous aggregate flow history'
              : 'No anonymous aggregate history';
  const alertMarkerSourceLabel = isMock
    ? 'simulation alert markers'
    : alertHistory.loading
      ? 'loading selected Operation alert history'
      : alertHistory.error
        ? 'selected Operation alert history unavailable'
        : playbackAlerts.length > 0
          ? 'alert history markers'
          : 'no alert history markers';
  const isHistoricalPlayback = !isMock && playbackTime !== PLAYBACK_LIVE;
  const snapshotScopeLabel = isHistoricalPlayback ? `At ${fmtHHMM(positionMs)}` : 'Current';
  const historicalFlowRows = useMemo(
    () => isHistoricalPlayback
      ? zones
        .map(zone => nearestFlowHistoryPoint(flowHistory.byZone[zone.id] ?? [], positionMs))
        .filter((point): point is FlowHistoryPoint => Boolean(point))
      : [],
    [flowHistory.byZone, isHistoricalPlayback, positionMs, zones],
  );
  const movementFlowRows: AggregateFlowMetric[] = isHistoricalPlayback ? historicalFlowRows.filter(hasFlowProjection) : flowRows;
  const movementDirectionRows: AggregateFlowMetric[] = isHistoricalPlayback ? historicalFlowRows.filter(hasDirectionProjection) : directionRows;
  const movementCongestionRows: AggregateFlowMetric[] = isHistoricalPlayback ? historicalFlowRows.filter(hasCongestionProjection) : congestionMetricRows;
  const flowChartSeries = useMemo(
    () => isMock ? [] : sampleFlowHistory(flowHistory.byZone, zones, rangeStartMs, rangeEndMs, MOCK_FLOW_BARS),
    [flowHistory.byZone, isMock, rangeEndMs, rangeStartMs, zones],
  );
  const historicalProjectionStatus = (rows: AggregateFlowMetric[]): MovementProjectionStatus => {
    if (flowHistory.loading && flowHistory.rows.length === 0) return 'loading';
    if (flowHistory.error) return 'error';
    return rows.length > 0 ? 'history' : 'missing';
  };
  const activeDirectionProjectionStatus: MovementProjectionStatus = isHistoricalPlayback
    ? historicalProjectionStatus(movementDirectionRows)
    : directionProjectionStatus;
  const activeCongestionProjectionStatus: MovementProjectionStatus = isHistoricalPlayback
    ? historicalProjectionStatus(movementCongestionRows)
    : congestionProjectionStatus;
  const flowEmptyLabel = isHistoricalPlayback
    ? 'No aggregate flow history at playback position'
    : flowHistory.loading && flowHistory.rows.length === 0
      ? 'Loading aggregate flow history'
      : flowHistory.error
        ? 'Aggregate flow history unavailable'
        : 'No aggregate flow history for selected Operation window';
  const directionEmptyLabel = isHistoricalPlayback
    ? 'No aggregate direction history at playback position'
    : 'No current direction data';
  const hasFlowProjectionAvailable = isMock || flowChartSeries.some(point => point.sourceCount > 0);
  const hasDirectionProjectionAvailable = isMock || movementDirectionRows.length > 0;

  useEffect(() => {
    // Entry pressure is the headline movement story, so default to the entry/exit gate when the Operation has one.
    const defaultZone = zones.find(z => z.type === 'entry_exit_gate') ?? zones[0];
    if (!zoneId && defaultZone) {
      setZoneId(defaultZone.id);
      return;
    }
    if (zoneId && !zones.some(z => z.id === zoneId) && defaultZone) setZoneId(defaultZone.id);
  }, [zoneId, zones]);

  useEffect(() => {
    if (!enabledRanges.includes(range)) {
      setRange(enabledRanges[enabledRanges.length - 1] ?? '1h');
      setPlaybackTime(PLAYBACK_LIVE);
      setPlaying(false);
    }
  }, [enabledRanges, range]);

  useEffect(() => {
    if (playbackWindow.source !== 'route') return;
    setPlaybackTime(0);
    setPlaying(false);
  }, [playbackWindow.endMs, playbackWindow.source, playbackWindow.startMs]);

  useEffect(() => {
    if (!playing) return undefined;
    if (playbackTime === PLAYBACK_LIVE) {
      setPlaying(false);
      return undefined;
    }
    const interval = window.setInterval(() => {
      setPlaybackTime(prev => {
        const next = Math.min(PLAYBACK_LIVE, prev + 1);
        if (next === PLAYBACK_LIVE) setPlaying(false);
        return next;
      });
    }, 500 / Number.parseInt(playbackSpeed, 10));
    return () => window.clearInterval(interval);
  }, [playbackSpeed, playbackTime, playing]);

  const activeDensity: Record<string, DensitySnapshot> = useMemo(() => {
    if (playbackTime === PLAYBACK_LIVE) return density;
    if (!isMock) {
      return Object.fromEntries(
        zones
          .map((z): [string, DensitySnapshot | null] => {
            const point = nearestDensityHistoryPoint(
              densityHistory.byZone[z.id] ?? [],
              positionMs,
              DENSITY_HISTORY_SAMPLE_TOLERANCE_MS,
            );
            return [z.id, point ? densityHistoryPointToSnapshot(point) : null];
          })
          .filter((entry): entry is [string, DensitySnapshot] => Boolean(entry[1])),
      );
    }
    return Object.fromEntries(
      zones
        .map((z): [string, DensitySnapshot | null | undefined] => [
          z.id,
          getHistoricalDensity(z.id, new Date(positionMs), MOCK_HISTORY) ?? density[z.id],
        ])
        .filter((entry): entry is [string, DensitySnapshot] => Boolean(entry[1]))
    );
  }, [density, densityHistory.byZone, isMock, playbackTime, positionMs, zones]);

  const avgUtil = densityAvg(activeDensity, zones);
  const inboundRate = isMock
    ? Math.round(avgUtil * 4)
    : Math.round(movementFlowRows.reduce((sum, row) => sum + (row.entryRate ?? 0), 0));
  const outboundRate = isMock
    ? Math.round(avgUtil * 1.55)
    : Math.round(movementFlowRows.reduce((sum, row) => sum + (row.exitRate ?? 0), 0));
  const netFlow = isMock
    ? inboundRate - outboundRate
    : Math.round(movementFlowRows.reduce((sum, row) => sum + (row.netFlow ?? ((row.entryRate ?? 0) - (row.exitRate ?? 0))), 0));
  const canShowFlowStats = isMock || movementFlowRows.length > 0;

  const congestionRows = (isMock
    ? (zones.length > 0 ? zones : MOCK_ZONES).map((z, i) => {
      const capacityUtilisation = activeDensity[z.id]?.capacityUtilisation ?? MOCK_DENSITY[z.id]?.capacityUtilisation ?? 0;
      const inbound = Math.round((MOCK_DASHBOARD.movementTrend.byZone.find(item => item.zoneId === z.id)?.inflow ?? capacityUtilisation * 4) / 4);
      const outbound = Math.max(1, Math.round(capacityUtilisation * (i === 0 ? 1.35 : i === 1 ? 1.05 : 1.0)));
      const ratio = inbound / Math.max(1, outbound);
      return {
        id: z.id,
        zone: z.name,
        code: zoneCode(z),
        in: inbound,
        out: outbound,
        ratio,
        dur: i === 0 ? '12 min' : i === 1 ? '8 min' : '5 min',
        level: congestionLevel(ratio * 30),
      };
    })
    : movementCongestionRows
      .map(row => {
        const zone = zones.find(item => item.id === row.zoneId);
        if (!zone) return null;
        const inbound = Math.round(safeRate(row.entryRate));
        const outbound = Math.round(safeRate(row.exitRate));
        const ratio = inbound / Math.max(1, outbound);
        const congestionScore = row.congestionScore ?? (row.congestionActive ? CONGESTION_ACTIVE_FALLBACK_SCORE : 0);
        return {
          id: zone.id,
          zone: zone.name,
          code: zoneCode(zone),
          in: inbound,
          out: outbound,
          ratio,
          dur: isHistoricalPlayback
            ? fmtHHMM(Date.parse(row.sampledAt ?? new Date(positionMs).toISOString()))
            : row.congestionActive ? 'live' : 'clear',
          level: congestionLevel(congestionScore),
        };
      })
      .filter((row): row is {
        id: string;
        zone: string;
        code: string;
        in: number;
        out: number;
        ratio: number;
        dur: string;
        level: 'high' | 'medium' | 'low';
      } => Boolean(row))
  ).sort((a, b) => b.ratio - a.ratio);
  const congestionPagination = useLocalPagination(congestionRows, 10);
  const visibleCongestionRows = congestionPagination.pageRows;
  const maxCongestionFlow = Math.max(1, ...congestionRows.flatMap(row => [row.in, row.out]));

  useEffect(() => {
    congestionPagination.resetPage();
  }, [congestionPagination.resetPage, isHistoricalPlayback, playbackTime, range, selectedOperationId]);

  function handleRange(next: PlaybackRange) {
    if (!enabledRanges.includes(next)) return;
    setRange(next);
    setPlaybackTime(PLAYBACK_LIVE);
    setPlaying(false);
    recordActivity({
      action: CROWD_CLIENT_ACTIVITY_ACTION.playbackChanged,
      page: '/movement',
      operationId: selectedOperationId,
      resourceType: CROWD_CLIENT_RESOURCE_TYPE.playback,
      resourceId: 'movement-playback',
      metadata: { windowPreset: next, playbackState: 'window_changed' },
    });
  }

  function handleSeek(value: number) {
    setPlaybackTime(value);
    setPlaying(false);
    recordActivity({
      action: CROWD_CLIENT_ACTIVITY_ACTION.playbackChanged,
      page: '/movement',
      operationId: selectedOperationId,
      resourceType: CROWD_CLIENT_RESOURCE_TYPE.playback,
      resourceId: 'movement-playback',
      metadata: { control: 'scrubber', playbackState: 'scrubbed' },
    });
  }

  function handlePlayPause() {
    setPlaying(prev => {
      if (!prev && playbackTime === PLAYBACK_LIVE) setPlaybackTime(0);
      const next = !prev;
      recordActivity({
        action: CROWD_CLIENT_ACTIVITY_ACTION.playbackChanged,
        page: '/movement',
        operationId: selectedOperationId,
        resourceType: CROWD_CLIENT_RESOURCE_TYPE.playback,
        resourceId: 'movement-playback',
        metadata: { control: 'play_pause', playbackState: next ? 'playing' : 'paused' },
      });
      return next;
    });
  }

  async function handleExportCsv() {
    const from = new Date(rangeStartMs).toISOString();
    const to = new Date(rangeEndMs).toISOString();
    recordActivity({
      action: CROWD_CLIENT_ACTIVITY_ACTION.drilldownOpened,
      page: '/movement',
      operationId: selectedOperationId,
      resourceType: CROWD_CLIENT_RESOURCE_TYPE.report,
      resourceId: 'zone_activity_summary',
      metadata: { sourcePage: '/movement', targetPage: '/reports', templateId: 'zone_activity_summary', format: 'csv' },
    });
    const params = new URLSearchParams({
      template: 'zone_activity_summary',
      format: 'csv',
      zoneId,
      from,
      to,
    });
    if (selectedOperationId) params.set('operationId', selectedOperationId);
    navigate(`/reports?${params.toString()}`);
  }

  return (
    <div style={{ flex: 1, overflowY: 'auto', padding: SPACE[20], fontFamily: "'Albert Sans', sans-serif" }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: SPACE[16] }}>
        <div>
          <div style={{ fontSize: FONT_SIZE.subheading, fontWeight: FONT_WEIGHT.bold, color: C.text, letterSpacing: LETTER_SPACING.tight }}>
            Crowd Movement & Historical Playback
          </div>
          <div style={{ fontSize: FONT_SIZE.basePlus, lineHeight: '15.4px', color: C.sub, marginTop: SPACE[2] }}>
            Entry/exit flow analytics for selected Operation zones. Playback is bounded to the selected Operation window.
          </div>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: SPACE[8], marginTop: SPACE[10] }}>
            <span style={{
              fontSize: FONT_SIZE.xs,
              color: C.amber,
              background: C.amberDim,
              border: `1px solid ${C.amber}44`,
              borderRadius: RADIUS.sm,
              padding: `${SPACE[4]}px ${SPACE[7]}px`,
              fontWeight: FONT_WEIGHT.bold,
              letterSpacing: LETTER_SPACING.wide,
              textTransform: 'uppercase',
            }}>
              {selectedOperation?.name ?? 'Select Operation'}
            </span>
            <span style={{
              fontSize: FONT_SIZE.xs,
              color: operationStatusColor(C, selectedOperation?.status),
              background: `${operationStatusColor(C, selectedOperation?.status)}18`,
              border: `1px solid ${operationStatusColor(C, selectedOperation?.status)}55`,
              borderRadius: RADIUS.sm,
              padding: `${SPACE[4]}px ${SPACE[7]}px`,
              fontWeight: FONT_WEIGHT.bold,
              letterSpacing: LETTER_SPACING.wide,
              textTransform: 'uppercase',
            }}>
              {operationStatusLabel(selectedOperation?.status)}
            </span>
            <span style={{
              fontSize: FONT_SIZE.xs,
              color: C.muted,
              background: C.faint,
              border: `1px solid ${C.border2}`,
              borderRadius: RADIUS.sm,
              padding: `${SPACE[4]}px ${SPACE[7]}px`,
              fontWeight: FONT_WEIGHT.bold,
              letterSpacing: LETTER_SPACING.wide,
              textTransform: 'uppercase',
            }}>
              {zones.length} operation zones
            </span>
            <span style={{
              display: 'inline-flex', alignItems: 'center', gap: SPACE[6],
              fontSize: FONT_SIZE.xs,
              color: C.text,
              background: C.faint,
              border: `1px solid ${C.border2}`,
              borderRadius: RADIUS.sm,
              padding: `${SPACE[4]}px ${SPACE[7]}px`,
              fontWeight: FONT_WEIGHT.bold,
              letterSpacing: LETTER_SPACING.wide,
              textTransform: 'uppercase',
            }}>
              <StatusDot
                color={playbackWindow.source === 'route' ? C.amber : C.green}
                pulse={playbackWindow.source !== 'route' && playbackTime === PLAYBACK_LIVE}
                size={5}
              />
              {playbackModeLabel}
            </span>
          </div>
        </div>
        <div style={{ display: 'flex', gap: SPACE[8] }}>
          <select
            value={selectedZone.id}
            onChange={e => {
              setZoneId(e.target.value);
              recordActivity({
                action: CROWD_CLIENT_ACTIVITY_ACTION.recordViewed,
                page: '/movement',
                operationId: selectedOperationId,
                resourceType: CROWD_CLIENT_RESOURCE_TYPE.zone,
                resourceId: e.target.value,
                metadata: { sourcePage: '/movement' },
              });
            }}
            style={{
              background: C.surface,
              border: `1px solid ${C.border2}`,
              borderRadius: RADIUS.md,
              padding: `${SPACE[7]}px ${SPACE[10]}px`,
              fontSize: FONT_SIZE.basePlus,
              color: C.text,
              fontFamily: "'Albert Sans', sans-serif",
              outline: 'none',
            }}
          >
            {zones.map(z => <option key={z.id} value={z.id}>{zoneCode(z)} - {z.name}</option>)}
          </select>
          <Btn C={C} variant="ghost" icon={<Icons.Export size={SIZE.iconSm} />} size="md" onClick={() => { void handleExportCsv(); }}>
            Export CSV
          </Btn>
        </div>
      </div>

      <Panel C={C} style={{ padding: SPACE[18], marginBottom: SPACE[14] }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: SPACE[12] }}>
          <div>
            <ScopeTitle C={C} segments={[{ text: 'Density Replay - ' }, { text: selectedZone.name, key: true }, { text: ' · ' }, { text: windowScopeLabel, key: true }]} />
            <div style={{ fontSize: FONT_SIZE.base, lineHeight: '14px', color: C.muted, marginTop: SPACE[3] }}>
              Showing {playbackWindowLabel}. Operation window: {operationWindowLabel}. {historySourceLabel}; {alertMarkerSourceLabel}.
            </div>
          </div>
          <div style={{ display: 'flex', gap: SPACE[6], alignItems: 'center' }}>
            <span style={{ fontSize: FONT_SIZE.basePlus, lineHeight: '14.7px', color: C.muted }}>Showing</span>
            {PLAYBACK_RANGES.map(r => {
              const disabled = !enabledRanges.includes(r);
              return (
                <button key={r} disabled={disabled} title={disabled ? 'Outside selected Operation window' : undefined} onClick={() => handleRange(r)} style={{
                  fontSize: FONT_SIZE.sm,
                  fontWeight: FONT_WEIGHT.bold,
                  padding: `${SPACE[4]}px ${SPACE[9]}px`,
                  borderRadius: RADIUS.sm,
                  background: r === range ? C.amberDim : 'transparent',
                  border: `1px solid ${r === range ? C.amber + '55' : C.border2}`,
                  color: disabled ? C.muted : r === range ? C.amber : C.sub,
                  cursor: disabled ? 'not-allowed' : 'pointer',
                  fontFamily: 'monospace',
                  opacity: disabled ? 0.5 : 1,
                }}>{r}</button>
              );
            })}
          </div>
        </div>
        <BigTimelineChart
          C={C}
          zoneId={selectedZone.id}
          alerts={playbackAlerts}
          playbackTime={playbackTime}
          playing={playing}
          playbackSpeed={playbackSpeed}
          startMs={rangeStartMs}
          endMs={rangeEndMs}
          historyByZone={densityHistory.byZone}
          isMock={isMock}
          historyLoading={densityHistory.loading}
          historyError={densityHistory.error}
          liveLabel={terminalPlaybackLabel}
          onPlayPause={handlePlayPause}
          onSeek={handleSeek}
          onSpeedChange={(speed) => {
            setPlaybackSpeed(speed);
            recordActivity({
              action: CROWD_CLIENT_ACTIVITY_ACTION.playbackChanged,
              page: '/movement',
              operationId: selectedOperationId,
              resourceType: CROWD_CLIENT_RESOURCE_TYPE.playback,
              resourceId: 'movement-playback',
              metadata: { control: 'speed', playbackSpeed: speed },
            });
          }}
        />
      </Panel>

      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: SPACE[14], marginBottom: SPACE[14] }}>
        <Panel C={C} style={{ padding: SPACE[18] }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: SPACE[12] }}>
            <ScopeTitle C={C} segments={[{ text: 'Entry / Exit Flow Rate · All Operation Zones · ' }, { text: windowScopeLabel, key: true }]} />
            <span style={{ fontSize: FONT_SIZE.base, lineHeight: '14px', color: C.muted }}>persons / min</span>
          </div>
          <EntryExitChart
            C={C}
            density={activeDensity}
            zones={zones}
            series={flowChartSeries}
            isMock={isMock}
            hasFlowProjectionAvailable={hasFlowProjectionAvailable}
            emptyLabel={flowEmptyLabel}
          />
          <div style={{ marginTop: SPACE[14], display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: SPACE[10] }}>
            <FlowStat C={C} label="Inbound rate" value={canShowFlowStats ? String(inboundRate) : 'N/A'} unit="ppl/min" color={C.teal} trend="up" />
            <FlowStat C={C} label="Outbound rate" value={canShowFlowStats ? String(outboundRate) : 'N/A'} unit="ppl/min" color={C.amber} trend="stable" />
            <FlowStat C={C} label="Net flow" value={canShowFlowStats ? `${netFlow >= 0 ? '+' : ''}${netFlow}` : 'N/A'} unit="ppl/min" color={C.ember} trend="up" />
          </div>
        </Panel>

        <Panel C={C} style={{ padding: SPACE[18] }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: SPACE[12] }}>
            <ScopeTitle C={C} segments={[{ text: 'Aggregate Movement Direction · ' }, { text: snapshotScopeLabel, key: true }]} />
            <div style={{ display: 'flex', alignItems: 'center', gap: SPACE[8] }}>
              <ProjectionStatusPill C={C} status={activeDirectionProjectionStatus} />
              <span style={{ fontSize: FONT_SIZE.base, lineHeight: '14px', color: C.muted }}>anonymous vectors</span>
            </div>
          </div>
          <DirectionalRose
            C={C}
            density={activeDensity}
            zones={zones}
            metrics={movementDirectionRows}
            isMock={isMock}
            hasDirectionProjectionAvailable={hasDirectionProjectionAvailable}
            emptyLabel={directionEmptyLabel}
          />
          <ProjectionStatusNote C={C} status={activeDirectionProjectionStatus} />
        </Panel>
      </div>

      <Panel C={C} style={{ padding: 0, marginBottom: SPACE[14] }}>
        <div style={{ padding: `${SPACE[14]}px ${SPACE[18]}px`, borderBottom: `1px solid ${C.border2}`, display: 'flex', justifyContent: 'space-between' }}>
          <ScopeTitle C={C} segments={[{ text: 'Congestion Detection · ' }, { text: snapshotScopeLabel, key: true }]} />
          <div style={{ display: 'flex', alignItems: 'center', gap: SPACE[8] }}>
            <ProjectionStatusPill C={C} status={activeCongestionProjectionStatus} />
            <span style={{ fontSize: FONT_SIZE.base, lineHeight: '14px', color: C.muted }}>Selected Operation zones</span>
          </div>
        </div>
        {congestionRows.length === 0 && (
          <div style={{ padding: `${SPACE[24]}px ${SPACE[18]}px`, color: C.sub, fontSize: FONT_SIZE.basePlus, lineHeight: '14.7px' }}>
            {zones.length === 0
              ? 'No zones assigned to the selected Operation.'
              : isHistoricalPlayback
                ? 'No aggregate congestion history at playback position.'
                : 'No current congestion data for selected Operation zones.'}
          </div>
        )}
        {visibleCongestionRows.map(c => (
          <div key={c.id} style={{ padding: `${SPACE[12]}px ${SPACE[18]}px`, borderBottom: `1px solid ${C.border}`, display: 'flex', alignItems: 'center', gap: SPACE[14] }}>
            <span style={{ fontSize: FONT_SIZE.xs, fontWeight: FONT_WEIGHT.bold, color: C.teal, letterSpacing: LETTER_SPACING.label, fontFamily: 'monospace' }}>{c.code}</span>
            <span style={{ fontSize: FONT_SIZE.body, fontWeight: FONT_WEIGHT.semibold, color: C.text, minWidth: 180 }}>{c.zone}</span>
            <div style={{ flex: 1, display: 'flex', alignItems: 'center', gap: SPACE[8] }}>
              <span style={{ fontSize: FONT_SIZE.base, color: C.teal, fontFamily: 'monospace', fontWeight: FONT_WEIGHT.semibold }}>In {c.in}</span>
              <div style={{ flex: 1, height: SIZE.barMd, background: C.faint, borderRadius: RADIUS.sm, overflow: 'hidden', position: 'relative' }}>
                <div style={{ position: 'absolute', left: 0, top: 0, height: '100%', width: `${Math.min(100, (c.in / maxCongestionFlow) * 100)}%`, background: C.teal }} />
              </div>
              <span style={{ fontSize: FONT_SIZE.base, color: C.amber, fontFamily: 'monospace', fontWeight: FONT_WEIGHT.semibold }}>Out {c.out}</span>
            </div>
            <span style={{
              fontSize: FONT_SIZE.xs,
              fontWeight: FONT_WEIGHT.bold,
              padding: `${SPACE[3]}px ${SPACE[7]}px`,
              borderRadius: RADIUS.sm,
              background: c.level === 'high' ? C.emberDim : c.level === 'medium' ? C.amberDim : C.faint,
              color: c.level === 'high' ? C.ember : c.level === 'medium' ? C.amber : C.muted,
              letterSpacing: LETTER_SPACING.wide,
              textTransform: 'uppercase',
            }}>{c.level} congestion</span>
            <span style={{ fontSize: FONT_SIZE.basePlus, lineHeight: '14.7px', color: C.sub, fontFamily: 'monospace', minWidth: 50, textAlign: 'right' }}>{c.dur}</span>
          </div>
        ))}
        <PaginationControls
          C={C}
          pagination={congestionPagination.pagination}
          loading={activeCongestionProjectionStatus === 'loading'}
          error={activeCongestionProjectionStatus === 'error' ? 'Movement data is unavailable.' : null}
          emptyLabel="No congestion rows available."
          onPageChange={congestionPagination.setPage}
          onPageSizeChange={congestionPagination.setPageSize}
        />
        <div style={{ padding: `0 ${SPACE[18]}px ${SPACE[12]}px` }}>
          <ProjectionStatusNote C={C} status={activeCongestionProjectionStatus} />
        </div>
      </Panel>

      <Panel C={C} style={{ padding: SPACE[18] }}>
        <ScopeTitle C={C} segments={[{ text: 'Density Heatmap - Selected Operation Zones, ' }, { text: windowScopeLabel, key: true }]} mb={6} />
        <ZoneHeatmap
          C={C}
          zones={zones}
          startMs={rangeStartMs}
          endMs={rangeEndMs}
          playbackTime={playbackTime}
          historyByZone={densityHistory.byZone}
          isMock={isMock}
          historyLoading={densityHistory.loading}
          historyError={densityHistory.error}
          cursorLabel={timelineCursorLabel}
        />
      </Panel>
    </div>
  );
}
